<template>
    <div>
        <base-modal
            ref="panel-editor-modal"
            :centered="false"
            :no-padding="true"
            size="large"
            data-panel-edit-modal="true"
            :loading="modalLoading"
        >
            <template>
                <!-- Header Tabs & Close Button -->
                <div class="border-bottom">
                    <div
                        class="d-flex align-items-center settings-nav-container rounded-top"
                    >
                        <div class="flex-grow-1 mr-sm-3 rounded">
                            <div class="d-flex">
                                <a
                                    class="settings-nav-link"
                                    :class="{ active: currentTab === 'content' }"
                                    @click="currentTab = 'content'"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-pencil align-middle mr-1"></span>
                                    <span class="align-middle">Content</span>
                                </a>
                                <a
                                    class="settings-nav-link"
                                    :class="{ active: currentTab === 'settings' }"
                                    @click="currentTab = 'settings'"
                                    v-show="selectedPanelHasExtraSettings"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-cogwheel align-middle mr-1"></span>
                                    <span class="align-middle">Settings</span>
                                </a>
                                <a
                                    class="settings-nav-link"
                                    :class="{ active: currentTab === 'reporting' }"
                                    @click="currentTab = 'reporting'"
                                    v-show="selectedPanelHasReporting"
                                >
                                    <span class="icon icon-100 icon-glyphicons-basic-stats-circle align-middle mr-1"></span>
                                    <span class="align-middle">Reporting</span>
                                </a>
                            </div>
                        </div>
                        <div class="flex-grow-0 d-flex align-items-center mb-3">
                            <info-popover
                                v-if="panelTypeWalkthroughVideoUrl(panel.panel_type)"
                                placement="bottom"
                                class="mr-3 pointer align-middle"
                                content="▶️ Click to watch a walkthrough video about this content type"
                            >
                                <a
                                    @click.stop
                                    class="badge badge-purple-highlight badge-small align-middle"
                                    :href="panelTypeWalkthroughVideoUrl(panel.panel_type)"
                                    target="_blank"
                                >
                                    <span class="icon icon-glyphicons-basic-video-play mr-1"></span>
                                    <span class="align-middle">Watch</span>
                                </a>
                            </info-popover>
                            <info-popover
                                v-if="panel.panel_type"
                                placement="bottom"
                                class="mr-3 align-middle pointer"
                                :content="'Content-Type: ' + panelTypeLabel(panel.panel_type)"
                            >
                                <span
                                    class="badge badge-light badge-small panel-type-badge align-middle"
                                    role="button"
                                    aria-label="Switch content type"
                                    @click="openChangePanelTypeModal()"
                                >
                                <span
                                    class="align-middle icon icon-lg mr-1"
                                    :class="[panelTypeIcon(panel.panel_type)]"
                                ></span>
                                <span class="align-middle">{{panelTypeLabel(panel.panel_type)}}</span>
                            </span>
                            </info-popover>
                            <button
                                type="button"
                                class="close d-flex align-items-center"
                                data-dismiss="modal"
                                aria-label="Close"
                                style=""
                            >
                                <span
                                    class="icon icon-glyphicons-basic-menu-close align-middle"
                                    style="font-size: 20px"
                                ></span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Main Modal Body -->
                <div class="px-4" style="padding-top: 2.15rem; padding-bottom: 1.75rem; min-height: 100px">
                    <!-- Content Tab -->
                    <div v-show="currentTab === 'content'">
                        <!-- Pre-Content Video -->
                        <div v-if="selectedPanelAllowsPreContentVideo" class="pb-4">
                            <base-form
                                v-model="panel"
                                :fields="preContentVideoFields"
                                :inline-style="true"
                            ></base-form>
                            <div v-show="panel.show_video_first" class="pt-1">
                                <rich-content-editor
                                    v-model="panel.preContentVideoElements"
                                    :allowed-element-types="['video',]"
                                    :allowed-video-fields="[
                                        'autoplay_video',
                                        'play_video_before_continue',
                                        'hide_video_controls',
                                        'display_full_screen',
                                    ]"
                                    :default-checked-options="['display_full_screen', 'autoplay_video']"
                                    :show-info-footers="false"
                                    :max-items="1"
                                    ref="choiceRichContentEditor"
                                ></rich-content-editor>
                            </div>
                        </div>
                        <!-- Header -->
                        <div v-if="selectedPanelAllowsHeader" class="pb-3">
                            <base-form v-model="panel" :fields="headerFields"></base-form>
                        </div>
                        <!-- Text Panel -->
                        <div v-else-if="panel.panel_type === 'text'">
                            <rich-content-editor
                                v-if="editModalIsOpen"
                                v-model="panel.elements"
                                :max-items="1"
                                :show-delete-btn="false"
                            ></rich-content-editor>
                        </div>
                        <!-- Image Panel -->
                        <div v-else-if="panel.panel_type === 'image'">
                            <rich-content-editor
                                v-if="editModalIsOpen"
                                v-model="panel.elements"
                                :max-items="1"
                                :allowed-element-types="['image']"
                            ></rich-content-editor>
                        </div>
                        <!-- Video Panel -->
                        <div v-else-if="panel.panel_type === 'video'">
                            <rich-content-editor
                                v-if="editModalIsOpen"
                                v-model="panel.elements"
                                :max-items="1"
                                :allowed-element-types="['video', 'vimeo', 'youtube',]"
                            ></rich-content-editor>
                        </div>
                        <!-- Audio Panel -->
                        <div v-else-if="panel.panel_type === 'audio'">
                            <rich-content-editor
                                v-if="editModalIsOpen"
                                v-model="panel.elements"
                                :max-items="1"
                                :allowed-element-types="['audio',]"
                            ></rich-content-editor>
                        </div>
                        <!-- PDF Panel -->
                        <div v-else-if="panel.panel_type === 'pdf'">
                            <rich-content-editor
                                v-if="editModalIsOpen"
                                v-model="panel.elements"
                                :max-items="1"
                                :allowed-element-types="['pdf',]"
                            ></rich-content-editor>
                        </div>
                        <!-- Custom/Combo Panel -->
                        <div v-if="panel.panel_type === 'custom'">
                            <rich-content-editor
                                v-if="editModalIsOpen"
                                v-model="panel.elements"
                                ref="richContentEditor"
                            ></rich-content-editor>
                        </div>
                        <!-- Tabs Panel -->
                        <div v-else-if="panel.panel_type === 'tabs'">
                            <p class="mb-0 font-weight-500">Layout Style</p>
                            <p class="small text-secondary">Choose how to display your tabbed-content.</p>
                            <div class="d-flex pb-5">
                                <div
                                    v-for="option in tabsLayoutOptions"
                                    :key="option.id"
                                    class="flex-grow-0 card h-100 panel-type-card panel-type-card-available"
                                    :class="{ 'panel-type-card-active': panel.display_variation == option.value }"
                                    style="margin-right: 20px"
                                >
                                    <div
                                        class="card-body panel-type-card-body rounded-lg tabs-layout-container"
                                        @click="panel.display_variation = option.value"
                                    >
                                        <img :src="option.iconPath" :alt="option.alt" height="70" class="mw-100" />
                                        <div class="tiny font-weight-500 text-secondary text-center mb-0 mt-2">
                                            {{ option.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <choice-panel-editor
                                v-model="panel"
                                :choices.sync="panel.choices"
                                :node-options="nodeOptions"
                            ></choice-panel-editor>
                        </div>
                        <!-- Buttons Panel -->
                        <div v-else-if="panel.panel_type === 'multichoice'">
                            <choice-panel-editor
                                v-model="panel"
                                :choices.sync="panel.choices"
                                :node-options="nodeOptions"
                            ></choice-panel-editor>
                        </div>
                        <!-- Text Response Panel -->
                        <div v-else-if="panel.panel_type === 'textarea'">
                            <div class="pt-2">
                                <div class="custom-control custom-switch pb-3">
                                    <input
                                        v-model="panel.enable_ai_analysis"
                                        type="checkbox"
                                        class="custom-control-input"
                                        :id="_uid + 'ai-analysis'"
                                    />
                                    <label class="custom-control-label" :for="_uid + 'ai-analysis'">
                                        AI Response Analysis
                                        <ai-badge :enableTooltip="false"></ai-badge>
                                        <div class="small text-secondary mt-1 mb-1">
                                            Give the learner feedback on their response.
                                        </div>
                                    </label>
                                </div>
                                <div
                                    v-if="panel.enable_ai_analysis && panel.ai_analysis_config"
                                    style="padding-left: 36px"
                                >
                                    <!-- Expert Response -->
                                    <div class="mb-2 mt-1">
                                        <span class="badge badge-gray mr-2 align-middle"
                                            ><span class="icon icon-glyphicons-basic-check align-middle mr-1"></span>
                                            <span class="align-middle">Correct Response</span>
                                            </span
                                        >
                                    </div>
                                    <p class="small text-secondary mb-3">
                                        Enter an <span class="font-weight-bold">ideal or accepted response</span>, if one exists.
                                        The AI will compare the learner's response with the one you enter below.
                                    </p>
                                    <textarea-auto-expand
                                        v-model="panel.ai_analysis_config.expert_response"
                                        class="form-control form-control-sm mb-4"
                                        placeholder="Enter an ideal or accepted response..."
                                        rows="5"
                                    ></textarea-auto-expand>
                                    <!-- Extra Instructions -->
                                    <div class="mb-2 pt-2">
                                        <span class="badge badge-gray mr-1 align-middle"
                                            ><span class="icon icon-glyphicons-basic-book align-middle mr-1"></span>
                                            <span class="align-middle">Extra Instructions & Context</span>
                                            </span
                                        >

                                    </div>
                                    <p class="small text-secondary mb-3">
                                        Add any <span class="font-weight-bold">additional info or instructions</span>
                                        to guide the way the AI generates feedback.
                                    </p>
                                    <textarea-auto-expand
                                        v-model="panel.ai_analysis_config.extra_instructions"
                                        class="form-control form-control-sm mb-4"
                                        placeholder="E.g. Check for proper grammar or spelling..."
                                        rows="5"
                                    ></textarea-auto-expand>
                                    <div class="mb-4 pb-2">
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-2 align-middle">
                                                <span class="icon icon-glyphicons-basic-paperclip align-middle mr-1"></span>
                                                <span class="align-middle">Reference Document</span>
                                            </span>
                                            <span class="badge badge-highlight badge-tiny mr-1 align-middle">
                                                <span class="icon icon-100 icon-glyphicons-basic-chemistry-alt mr-xs"></span>
                                                <span class="align-middle">Beta</span>
                                            </span>
                                        </div>
                                        <div class="small text-secondary mb-3">
                                            Upload a reference file to help the AI generate accurate feedback.
                                        </div>
                                        <file-upload
                                            v-model="referenceDocumentUpload"
                                            :directUpload="true"
                                            placeholder="Drop a .pdf or .docx file here"
                                            accept=".pdf|.docx"
                                            file-type-label="document"
                                            size="sm"
                                            :show-upload-status="true"
                                            @upload-started="referenceDocumentUploading = true; clearReferenceDocumentStatusTimeouts()"
                                            @upload-complete="referenceDocumentUploading = false; startReferenceDocumentConversion()"
                                            @upload-failed="referenceDocumentUploading = false"
                                            @upload-ended="referenceDocumentUploading = false"
                                        ></file-upload>
                                        <div
                                            v-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'in_progress'
                                            "
                                            class="small text-light-wave mt-2"
                                        >
                                            Reading document...
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'successful'
                                            "
                                            class="small mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-circle-check text-success icon-mr"></span>
                                            <span>Document Processed</span>
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'failed'
                                            "
                                            class="small text-warning mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-triangle-alert icon-mr"></span>
                                            <span>Document processing failed.</span>
                                        </div>
                                    </div>
                                    <base-form
                                        v-model="panel.ai_analysis_config"
                                        :fields="aiAnalysisFields"
                                        :inline-style="true"
                                        class="py-2"
                                    ></base-form>
                                    <base-form
                                        v-model="panel"
                                        :fields="aiAnalysisScoreFields"
                                        :inline-style="true"
                                        class="mb-3"
                                    ></base-form>
                                    <!-- Scoring Rubric -->
                                    <div v-show="panel.enable_ai_scoring">
                                        <!-- Extra Instructions -->
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-1 align-middle"
                                                ><span class="icon icon-glyphicons-basic-certificate align-middle mr-1"></span>
                                                <span class="align-middle">Add Scoring Rubric</span>
                                                </span
                                            >
                                        </div>
                                        <p class="small text-secondary mb-3">
                                            Tell the AI <span class="font-weight-bold">how to score</span> the learner's response.
                                        </p>
                                        <textarea-auto-expand
                                            v-model="panel.ai_analysis_config.scoring_rubric"
                                            class="form-control form-control-sm"
                                            placeholder="E.g. Award the learner one point for each safety hazard identified..."
                                            rows="5"
                                        ></textarea-auto-expand>
                                        <div class="d-flex align-items-center mt-3">
                                            <div class="flex-grow-0 mr-2 medium">Maximum Achievable Points:</div>
                                            <div class="flex-grow-0">
                                                <input
                                                    type="number"
                                                    v-model="panel.max_ai_score"
                                                    class="form-control form-control-sm"
                                                    style="max-width: 60px"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Custom GPT Panel -->
                        <div v-else-if="panel.panel_type === 'chat_v2'">
                            <!-- Persona -->
                            <div class="mb-4">
                                <div class="mb-2">
                                    <span class="badge badge-gray mr-2 align-middle"
                                        ><span class="icon icon-glyphicons-basic-user align-middle"></span> Persona &
                                        Knowledge</span
                                    >
                                </div>
                                <p class="small text-secondary mb-3">
                                    Describe the person you want to simulate. Include <strong>all details</strong> the
                                    AI should know and be able to respond to.
                                </p>
                                <textarea-auto-expand
                                    v-model="panel.ai_analysis_config.persona_details"
                                    class="form-control form-control-sm mb-1"
                                    placeholder="You are..."
                                    rows="10"
                                ></textarea-auto-expand>
                                <div class="mb-3 d-flex align-items-center">
                                    <button
                                        class="btn btn-sm btn-link p-1 small mr-1"
                                        :disabled="panel.isGeneratingPersona"
                                        role="button"
                                        @click="generateChatPersona(panel)"
                                    >
                                        <span
                                            class="align-middle mr-xs"
                                            :class="{ 'text-light-wave': panel.isGeneratingPersona }"
                                        >
                                            {{ generateChatPersonaBtnLabel }}
                                        </span>
                                        <img
                                            v-show="!panel.isGeneratingPersona"
                                            src="/static/general/img/icons/shines.svg"
                                            height="11"
                                            class="align-middle"
                                        />
                                    </button>
                                    <div v-show="panel.isGeneratingPersona">
                                        <div class="custom-spinner custom-spinner-sm" role="status">
                                            <span class="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Extra Instructions -->
                            <div class="mt-2 mb-4 pb-2">
                                <div class="mb-2">
                                    <span
                                        v-if="
                                            panel.show_extra_instructions || panel.ai_analysis_config.extra_instructions
                                        "
                                        class="badge badge-gray"
                                    >
                                        <span class="icon icon-glyphicons-basic-book mr-1"></span>
                                        <span>Extra Instructions & Context</span>
                                    </span>
                                    <a
                                        v-else
                                        class="btn btn-sm badge badge-gray"
                                        @click="$set(panel, 'show_extra_instructions', true)"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-plus mr-1"></span>
                                        <span>Add Extra Instructions</span>
                                    </a>
                                </div>
                                <div
                                    v-if="panel.show_extra_instructions || panel.ai_analysis_config.extra_instructions"
                                >
                                    <p class="small text-secondary mb-3">
                                        Add any <span class="font-weight-bold">additional info or instructions</span> to guide the way the AI generates feedback.
                                    </p>
                                    <div class="mt-3">
                                        <textarea-auto-expand
                                            v-model="panel.ai_analysis_config.extra_instructions"
                                            class="form-control form-control-sm mb-1"
                                            placeholder="E.g. If the learner asks the same question twice, get upset/impatient..."
                                            rows="6"
                                        ></textarea-auto-expand>
                                    </div>
                                </div>
                            </div>
                            <!-- Key Interactions -->
                            <div>
                                <div class="custom-control custom-switch pb-3">
                                    <input
                                        v-model="showKeyInteractions"
                                        type="checkbox"
                                        class="custom-control-input"
                                        :id="_uid + 'key-interactions'"
                                    />
                                    <label class="custom-control-label" :for="_uid + 'key-interactions'">
                                        Key Interactions
                                        <ai-badge :enableTooltip="false"></ai-badge>
                                        <div class="small text-secondary mt-1 mb-1">
                                            Give the learner real-time feedback by flagging specific moments during the conversation.
                                        </div>
                                    </label>
                                </div>
                                <div v-if="showKeyInteractions" style="padding-left: 36px" class="mb-4">
                                    <div v-for="(intent, index) in panel.chat_intents" :key="index">
                                        <div v-show="!intent.is_deleted" class="rounded bg-offwhite p-3 my-4">
                                            <div class="row pb-3">
                                                <div class="col-4">
                                                    <div class="pb-2 small font-weight-500">Label</div>
                                                    <input
                                                        v-model="intent.message"
                                                        class="form-control form-control-sm"
                                                        type="text"
                                                        placeholder="Label (e.g. Age)"
                                                        ref="intentMessage"
                                                        maxlength="30"
                                                    />
                                                </div>
                                                <div class="col-4">
                                                    <div class="pb-2 small font-weight-500">Correct/Incorrect</div>
                                                    <select v-model="intent.correct" class="form-control form-control-sm">
                                                        <option :value="true">Correct</option>
                                                        <option :value="false">Incorrect</option>
                                                        <option :value="null">Neutral</option>
                                                    </select>
                                                </div>
                                                <div class="col-4">
                                                    <div class="pb-2 small font-weight-500">Score</div>
                                                    <input
                                                        v-model="intent.score"
                                                        class="form-control form-control-sm"
                                                        type="text"
                                                        placeholder="Score"
                                                    />
                                                </div>
                                            </div>
                                            <div class="pb-2">
                                                <div class="pb-2 small font-weight-500">
                                                    Briefly describe what the learner should ask or say
                                                </div>
                                                <textarea-auto-expand
                                                    v-model="intent.ai_description"
                                                    class="form-control form-control-sm"
                                                    rows="2"
                                                    placeholder="E.g. patient age, pain level..."
                                                ></textarea-auto-expand>
                                            </div>
                                            <div
                                                class="pb-2 pt-1"
                                                v-show="intent.feedback_message || intent.showFeedbackInput"
                                            >
                                                <div class="pb-2 small font-weight-500">
                                                    Add a feedback message to display if this interaction is activated
                                                </div>
                                                <textarea-auto-expand
                                                    v-model="intent.feedback_message"
                                                    class="form-control form-control-sm"
                                                    rows="2"
                                                    ref="intentFeedbackMessageInput"
                                                ></textarea-auto-expand>
                                            </div>
                                            <div class="d-flex align-items-center mt-2">
                                                <div class="flex-grow-1">
                                                    <button
                                                        v-show="!(intent.feedback_message || intent.showFeedbackInput)"
                                                        class="btn btn-sm btn-link p-1 tiny mr-1"
                                                        @click="showIntentFeedbackField(intent, index)"
                                                    >
                                                        + Feedback Message
                                                    </button>
                                                </div>
                                                <div class="flex-grow-0">
                                                    <button
                                                        @click="shiftListItem(index, -1, panel.chat_intents)"
                                                        class="btn btn-transparent p-1"
                                                    >
                                                        <span class="icon icon-100 icon-glyphicons-basic-arrow-up"></span>
                                                    </button>
                                                    <button
                                                        @click="shiftListItem(index, 1, panel.chat_intents)"
                                                        class="btn btn-transparent p-1"
                                                    >
                                                        <span class="icon icon-100 icon-glyphicons-basic-arrow-down"></span>
                                                    </button>
                                                    <button @click="removeIntent(intent)" class="btn btn-transparent p-1">
                                                        <span class="icon icon-100 icon-glyphicons-basic-bin"></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        @click="createIntent(panel.chat_intents.length + 1)"
                                        class="btn btn-sm btn-link p-0"
                                        role="button"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-plus mr-1"></span>
                                        <span>Key Interaction</span>
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div class="custom-control custom-switch pb-3 pt-3">
                                    <input
                                        v-model="panel.enable_ai_analysis"
                                        type="checkbox"
                                        class="custom-control-input"
                                        :id="_uid + 'ai-analysis'"
                                    />
                                    <label class="custom-control-label" :for="_uid + 'ai-analysis'">
                                        AI Response Analysis
                                        <ai-badge :enableTooltip="false"></ai-badge>
                                        <div class="small text-secondary mt-1 mb-1">
                                            Give the learner feedback on their conversation.
                                        </div>
                                    </label>
                                </div>
                                <!-- Evaluation & Scoring -->
                                <div
                                    v-if="panel.enable_ai_analysis && panel.ai_analysis_config"
                                    style="padding-left: 36px"
                                >
                                    <div class="mb-2">
                                        <span class="badge badge-gray mr-2 align-middle"
                                            ><span class="icon icon-glyphicons-basic-certificate align-middle mr-1"></span>
                                            <span class="align-middle">Add evaluation criteria</span></span
                                        >
                                    </div>
                                    <div class="small text-secondary mb-3">
                                        Tell the AI <span class="font-weight-bold">how to provide feedback</span> to the learner about their conversation.
                                    </div>
                                    <textarea-auto-expand
                                        v-model="panel.ai_analysis_config.feedback_rubric"
                                        class="form-control form-control-sm mb-5"
                                        placeholder="Add instructions for the AI..."
                                        rows="5"
                                    ></textarea-auto-expand>
                                    <div class="mb-4 pb-2">
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-2 align-middle">
                                                <span class="icon icon-glyphicons-basic-paperclip align-middle mr-1"></span>
                                                <span class="align-middle">Reference Document</span>
                                            </span>
                                            <span class="badge badge-highlight badge-tiny mr-1 align-middle">
                                                <span class="icon icon-100 icon-glyphicons-basic-chemistry-alt mr-xs"></span>
                                                <span class="align-middle">Beta</span>
                                            </span>
                                        </div>
                                        <div class="small text-secondary mb-3">
                                            Upload a reference file to help the AI generate accurate feedback.
                                        </div>
                                        <file-upload
                                            v-model="referenceDocumentUpload"
                                            :directUpload="true"
                                            placeholder="Drop a .pdf or .docx file here"
                                            accept=".pdf|.docx"
                                            file-type-label="document"
                                            size="sm"
                                            :show-upload-status="true"
                                            @upload-started="referenceDocumentUploading = true; clearReferenceDocumentStatusTimeouts()"
                                            @upload-complete="referenceDocumentUploading = false; startReferenceDocumentConversion()"
                                            @upload-failed="referenceDocumentUploading = false"
                                            @upload-ended="referenceDocumentUploading = false"
                                        ></file-upload>
                                        <div
                                            v-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'in_progress'
                                            "
                                            class="small text-light-wave mt-2"
                                        >
                                            Reading document...
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'successful'
                                            "
                                            class="small mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-circle-check text-success icon-mr"></span>
                                            <span>Document Processed</span>
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'failed'
                                            "
                                            class="small text-warning mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-triangle-alert icon-mr"></span>
                                            <span>Document processing failed.</span>
                                        </div>
                                    </div>
                                    <base-form
                                        v-model="panel.ai_analysis_config"
                                        :fields="aiAnalysisFields"
                                        :inline-style="true"
                                        class="py-2"
                                    ></base-form>
                                    <base-form
                                        v-model="panel"
                                        :fields="aiAnalysisScoreFields"
                                        :inline-style="true"
                                        class="mb-3"
                                    ></base-form>
                                    <div v-show="panel.enable_ai_scoring">
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-2 align-middle"
                                                ><span class="icon icon-glyphicons-basic-medal align-middle"></span> Add
                                                a scoring rubric</span
                                            >
                                        </div>
                                        <p class="small text-secondary mb-3">
                                            Tell the AI <span class="font-weight-bold">how to score</span> the learner's conversation.
                                        </p>
                                        <textarea-auto-expand
                                            v-model="panel.ai_analysis_config.scoring_rubric"
                                            class="form-control form-control-sm"
                                            placeholder="Award one point for a professional introduction, and one point for..."
                                            rows="5"
                                        ></textarea-auto-expand>
                                        <div class="d-flex align-items-center mt-3">
                                            <div class="flex-grow-0 mr-2 medium">Maximum Achievable Points:</div>
                                            <div class="flex-grow-0">
                                                <input
                                                    type="number"
                                                    v-model="panel.max_ai_score"
                                                    class="form-control form-control-sm"
                                                    style="max-width: 60px"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Voice Conversation Panel -->
                        <div v-else-if="panel.panel_type === 'voice_conversation'">
                            <!-- Persona & Knowledge -->
                            <div class="mb-4 mt-1">
                                <div class="mb-2">
                                    <span class="badge badge-gray mr-2 align-middle">
                                        <span class="icon icon-glyphicons-basic-user align-middle mr-1"></span>
                                        <span class="align-middle">Persona & Knowledge</span>
                                    </span>
                                </div>
                                <p class="small text-secondary mb-3">
                                    Describe the person you want to simulate. Include <strong>all details</strong> the
                                    AI should know and be able to respond to.
                                </p>
                                <textarea-auto-expand
                                    v-model="panel.ai_analysis_config.persona_details"
                                    class="form-control form-control-sm mb-1"
                                    placeholder="You are..."
                                    rows="10"
                                ></textarea-auto-expand>
                                <div class="mb-3 d-flex align-items-center">
                                    <button
                                        class="btn btn-sm btn-link p-1 small mr-1"
                                        :disabled="panel.isGeneratingPersona"
                                        role="button"
                                        @click="generateChatPersona(panel)"
                                    >
                                        <span
                                            class="align-middle mr-xs"
                                            :class="{ 'text-light-wave': panel.isGeneratingPersona }"
                                        >
                                            {{ generateChatPersonaBtnLabel }}
                                        </span>
                                        <img
                                            v-show="!panel.isGeneratingPersona"
                                            src="/static/general/img/icons/shines.svg"
                                            height="11"
                                            class="align-middle"
                                        />
                                    </button>
                                    <div v-show="panel.isGeneratingPersona">
                                        <div class="custom-spinner custom-spinner-sm" role="status">
                                            <span class="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Extra Instructions -->
                            <div class="mt-2 mb-4 pb-2">
                                <div class="mb-2">
                                    <span
                                        v-if="
                                            panel.show_extra_instructions || panel.ai_analysis_config.extra_instructions
                                        "
                                        class="badge badge-gray"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-book mr-1"></span>
                                        <span class="align-middle">Extra Instructions & Context</span>
                                    </span>
                                    <a
                                        v-else
                                        class="btn btn-sm badge badge-gray"
                                        @click="$set(panel, 'show_extra_instructions', true)"
                                    >
                                        <span class="icon icon-100 icon-glyphicons-basic-plus mr-1"></span>
                                        <span>Add Extra Instructions</span>
                                    </a>
                                </div>
                                <div
                                    v-if="panel.show_extra_instructions || panel.ai_analysis_config.extra_instructions"
                                >
                                    <p class="small text-secondary mb-3">
                                        Add any <span class="font-weight-bold">additional info or instructions</span> to guide the way the AI responds to learners.
                                    </p>
                                    <div class="mt-3">
                                        <textarea-auto-expand
                                            v-model="panel.ai_analysis_config.extra_instructions"
                                            class="form-control form-control-sm mb-1"
                                            placeholder="E.g. If the learner asks the same question twice, get upset/impatient..."
                                            rows="6"
                                        ></textarea-auto-expand>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div class="custom-control custom-switch pb-3">
                                    <input
                                        v-model="panel.enable_ai_analysis"
                                        type="checkbox"
                                        class="custom-control-input"
                                        :id="_uid + 'ai-analysis'"
                                    />
                                    <label class="custom-control-label" :for="_uid + 'ai-analysis'">
                                        AI Response Analysis
                                        <ai-badge :enableTooltip="false"></ai-badge>
                                        <div class="small text-secondary mt-1 mb-1">
                                            Give the learner feedback on their conversation.
                                        </div>
                                    </label>
                                </div>
                                <!-- Evaluation & Scoring -->
                                <div
                                    v-if="panel.enable_ai_analysis && panel.ai_analysis_config"
                                    style="padding-left: 36px"
                                >
                                    <div class="mb-2">
                                        <span class="badge badge-gray mr-2 align-middle"
                                            ><span class="icon icon-glyphicons-basic-certificate align-middle mr-1"></span>
                                            <span class="align-middle">Add evaluation criteria</span></span
                                        >
                                    </div>
                                    <div class="small text-secondary mb-3">
                                        Tell the AI <span class="font-weight-bold">how to provide feedback</span> to the learner about their conversation.
                                    </div>
                                    <textarea-auto-expand
                                        v-model="panel.ai_analysis_config.feedback_rubric"
                                        class="form-control form-control-sm mb-5"
                                        placeholder="Add instructions for the AI..."
                                        rows="5"
                                    ></textarea-auto-expand>
                                    <div class="mb-4 pb-2">
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-2 align-middle">
                                                <span class="icon icon-glyphicons-basic-paperclip align-middle mr-1"></span>
                                                <span class="align-middle">Reference Document</span>
                                            </span>
                                            <span class="badge badge-highlight badge-tiny mr-1 align-middle">
                                                <span class="icon icon-100 icon-glyphicons-basic-chemistry-alt mr-xs"></span>
                                                <span class="align-middle">Beta</span>
                                            </span>
                                        </div>
                                        <div class="small text-secondary mb-3">
                                            Upload a reference file to help the AI generate accurate feedback.
                                        </div>
                                        <file-upload
                                            v-model="referenceDocumentUpload"
                                            :directUpload="true"
                                            placeholder="Drop a .pdf or .docx file here"
                                            accept=".pdf|.docx"
                                            file-type-label="document"
                                            size="sm"
                                            :show-upload-status="true"
                                            @upload-started="referenceDocumentUploading = true; clearReferenceDocumentStatusTimeouts()"
                                            @upload-complete="referenceDocumentUploading = false; startReferenceDocumentConversion()"
                                            @upload-failed="referenceDocumentUploading = false"
                                            @upload-ended="referenceDocumentUploading = false"
                                        ></file-upload>
                                        <div
                                            v-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'in_progress'
                                            "
                                            class="small text-light-wave mt-2"
                                        >
                                            Reading document...
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'successful'
                                            "
                                            class="small mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-circle-check text-success icon-mr"></span>
                                            <span>Document Processed</span>
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'failed'
                                            "
                                            class="small text-warning mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-triangle-alert icon-mr"></span>
                                            <span>Document processing failed.</span>
                                        </div>
                                    </div>
                                    <base-form
                                        v-model="panel.ai_analysis_config"
                                        :fields="aiAnalysisFields"
                                        :inline-style="true"
                                        class="py-2"
                                    ></base-form>
                                    <base-form
                                        v-model="panel"
                                        :fields="aiAnalysisScoreFields"
                                        :inline-style="true"
                                        class="mb-3"
                                    ></base-form>
                                    <div v-show="panel.enable_ai_scoring">
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-2 align-middle"
                                                ><span class="icon icon-glyphicons-basic-medal align-middle"></span> Add
                                                a scoring rubric</span
                                            >
                                        </div>
                                        <p class="small text-secondary mb-3">
                                            Tell the AI <span class="font-weight-bold">how to score</span> the learner's conversation.
                                        </p>
                                        <textarea-auto-expand
                                            v-model="panel.ai_analysis_config.scoring_rubric"
                                            class="form-control form-control-sm"
                                            placeholder="Award one point for a professional introduction, and one point for..."
                                            rows="5"
                                        ></textarea-auto-expand>
                                        <div class="d-flex align-items-center mt-3">
                                            <div class="flex-grow-0 mr-2 medium">Maximum Achievable Points:</div>
                                            <div class="flex-grow-0">
                                                <input
                                                    type="number"
                                                    v-model="panel.max_ai_score"
                                                    class="form-control form-control-sm"
                                                    style="max-width: 60px"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Voice Recording Panel -->
                        <div v-else-if="panel.panel_type === 'voice_recording'">
                            <div class="pt-0">
                                <div class="custom-control custom-switch pb-3">
                                    <input
                                        v-model="panel.transcribe_audio"
                                        type="checkbox"
                                        class="custom-control-input"
                                        :id="_uid + 'ai-transcription'"
                                    />
                                    <label class="custom-control-label" :for="_uid + 'ai-transcription'">
                                        <span class="align-middle mr-2">Transcribe voice to text</span>
                                        <ai-badge :enableTooltip="false"></ai-badge>
                                        <div class="small text-secondary mt-1 mb-1">
                                            Transcribe the learner's voice recordings to text
                                        </div>
                                    </label>
                                </div>
                                <div class="custom-control custom-switch pb-3">
                                    <input
                                        v-model="panel.enable_ai_analysis"
                                        type="checkbox"
                                        class="custom-control-input"
                                        :id="_uid + 'ai-analysis'"
                                    />
                                    <label class="custom-control-label" :for="_uid + 'ai-analysis'">
                                        <span class="align-middle mr-2">AI Response Analysis</span>
                                        <ai-badge :enableTooltip="false"></ai-badge>
                                        <div class="small text-secondary mt-1 mb-1">
                                            Give the learner feedback on their response.
                                        </div>
                                    </label>
                                </div>
                                <div
                                    v-if="panel.enable_ai_analysis && panel.ai_analysis_config"
                                    style="padding-left: 36px"
                                >
                                    <!-- Expert Response -->
                                    <div class="mb-2 mt-1">
                                        <span class="badge badge-gray mr-2 align-middle"
                                            ><span class="icon icon-glyphicons-basic-check align-middle mr-1"></span>
                                            <span class="align-middle">Correct Response</span>
                                            </span
                                        >
                                    </div>
                                    <p class="small text-secondary mb-3">
                                        Enter an <span class="font-weight-bold">ideal or accepted response</span>, if one exists.
                                        The AI will compare the learner's response with the response you enter below.
                                    </p>
                                    <textarea-auto-expand
                                        v-model="panel.ai_analysis_config.expert_response"
                                        class="form-control form-control-sm mb-4"
                                        placeholder="Enter an ideal or accepted response..."
                                        rows="5"
                                    ></textarea-auto-expand>
                                    <!-- Extra Instructions -->
                                    <div class="mb-2 pt-2">
                                        <span class="badge badge-gray mr-2 align-middle"
                                            ><span class="icon icon-glyphicons-basic-book align-middle mr-1"></span>
                                            <span class="align-middle">Extra Instructions & Context</span>
                                            </span
                                        >
                                    </div>
                                    <p class="small text-secondary mb-3">
                                        Add any <span class="font-weight-bold">additional info or instructions</span>
                                        to guide the way the AI generates feedback.
                                    </p>
                                    <textarea-auto-expand
                                        v-model="panel.ai_analysis_config.extra_instructions"
                                        class="form-control form-control-sm mb-4"
                                        placeholder="E.g. Make sure the learner introduces themselves..."
                                        rows="5"
                                    ></textarea-auto-expand>
                                    <div class="mb-4 pb-2">
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-2 align-middle">
                                                <span class="icon icon-glyphicons-basic-paperclip align-middle mr-1"></span>
                                                <span class="align-middle">Reference Document</span>
                                            </span>
                                            <span class="badge badge-highlight badge-tiny mr-1 align-middle">
                                                <span class="icon icon-100 icon-glyphicons-basic-chemistry-alt mr-xs"></span>
                                                <span class="align-middle">Beta</span>
                                            </span>
                                        </div>
                                        <div class="small text-secondary mb-3">
                                            Upload a reference file to help the AI generate accurate feedback.
                                        </div>
                                        <file-upload
                                            v-model="referenceDocumentUpload"
                                            :directUpload="true"
                                            placeholder="Drop a .pdf or .docx file here"
                                            accept=".pdf|.docx"
                                            file-type-label="document"
                                            size="sm"
                                            :show-upload-status="true"
                                            @upload-started="referenceDocumentUploading = true; clearReferenceDocumentStatusTimeouts()"
                                            @upload-complete="referenceDocumentUploading = false; startReferenceDocumentConversion()"
                                            @upload-failed="referenceDocumentUploading = false"
                                            @upload-ended="referenceDocumentUploading = false"
                                        ></file-upload>
                                        <div
                                            v-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'in_progress'
                                            "
                                            class="small text-light-wave mt-2"
                                        >
                                            Reading document...
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'successful'
                                            "
                                            class="small mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-circle-check text-success icon-mr"></span>
                                            <span>Document Processed</span>
                                        </div>
                                        <div
                                            v-else-if="
                                                referenceDocumentUpload &&
                                                referenceDocumentUpload.file_processing_status === 'failed'
                                            "
                                            class="small text-warning mt-2"
                                        >
                                            <span class="icon icon-glyphicons-basic-triangle-alert icon-mr"></span>
                                            <span>Document processing failed.</span>
                                        </div>
                                    </div>
                                    <base-form
                                        v-model="panel.ai_analysis_config"
                                        :fields="aiAnalysisFields"
                                        :inline-style="true"
                                        class="py-2"
                                    ></base-form>
                                    <base-form
                                        v-model="panel"
                                        :fields="aiAnalysisScoreFields"
                                        :inline-style="true"
                                        class="mb-3"
                                    ></base-form>
                                    <!-- Scoring Rubric -->
                                    <div v-show="panel.enable_ai_scoring">
                                        <!-- Extra Instructions -->
                                        <div class="mb-2">
                                            <span class="badge badge-gray mr-1 align-middle"
                                                ><span class="icon icon-glyphicons-basic-certificate align-middle mr-1"></span>
                                                <span class="align-middle">Add Scoring Rubric</span>
                                                </span
                                            >
                                        </div>
                                        <p class="small text-secondary mb-3">
                                            Tell the AI <span class="font-weight-bold">how to score</span> the learner's response.
                                        </p>
                                        <textarea-auto-expand
                                            v-model="panel.ai_analysis_config.scoring_rubric"
                                            class="form-control form-control-sm"
                                            placeholder="Award one point for a professional introduction, and one point for..."
                                            rows="5"
                                        ></textarea-auto-expand>
                                        <div class="d-flex align-items-center mt-3">
                                            <div class="flex-grow-0 mr-2 medium">Maximum Achievable Points:</div>
                                            <div class="flex-grow-0">
                                                <input
                                                    type="number"
                                                    v-model="panel.max_ai_score"
                                                    class="form-control form-control-sm"
                                                    style="max-width: 60px"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Image Hotspots Panel -->
                        <div v-else-if="panel.panel_type === 'image_hotspots'">
                            <p class="h6 mb-2">Step 1: Add an Image</p>
                            <p class="text-secondary small mb-4">Upload or generate an image.</p>
                            <rich-content-editor
                                v-model="panel.elements"
                                :allowed-element-types="['image']"
                                :allowed-image-fields="['accessibility_alt_text']"
                                :show-info-footers="false"
                                :max-items="1"
                                :max-image-bytes="40000000"
                                :warning-image-bytes="30000000"
                            ></rich-content-editor>
                            <div v-if="panel.id && panelHasElements && panel.elements[0].stored_file">
                                <p class="h6 mb-4 mt-4 pt-2">Step 2: Select an image type</p>
                                <base-form
                                    v-model="panel"
                                    :fields="imageHotspotsFormFields"
                                    :inline-style="false"
                                    :condensed-style="false"
                                ></base-form>
                                <p class="h6 mb-2 mt-4 pt-2">Step 3: Add Interactivity</p>
                                <p class="text-secondary small mb-3">
                                    Add interactive spots to your image using the editor.
                                </p>
                                <p class="mb-2">
                                    <button class="btn btn-link px-0" @click="navToHotspotsEditor()">
                                        <span class="icon icon-glyphicons-basic-square-edit icon-mr"></span>
                                        <span v-if="!editLocked && panelIsEdited">Save & View Editor</span>
                                        <span v-else>View Interactivity Editor</span>
                                    </button>
                                </p>
                            </div>
                        </div>
                        <!-- Multiple Selection Panel -->
                        <div v-else-if="panel.panel_type === 'multiselect'">
                            <choice-panel-editor
                                v-model="panel"
                                :choices.sync="panel.choices"
                                :node-options="nodeOptions"
                            ></choice-panel-editor>
                        </div>
                        <!-- Single Selection Panel -->
                        <div v-else-if="panel.panel_type === 'singleselect'">
                            <choice-panel-editor
                                v-model="panel"
                                :choices.sync="panel.choices"
                                :node-options="nodeOptions"
                            ></choice-panel-editor>
                        </div>
                        <!-- Step Order Panel -->
                        <div v-else-if="panel.panel_type === 'step_order'">
                            <choice-panel-editor
                                v-model="panel"
                                :choices.sync="panel.choices"
                                :node-options="nodeOptions"
                            ></choice-panel-editor>
                        </div>
                        <!-- Categories Panel -->
                        <div v-else-if="panel.panel_type === 'categories'">
                            <div class="mb-4">
                                <p class="mb-2">Layout Style</p>
                                <div class="d-flex">
                                    <div
                                        v-for="option in categoriesLayoutOptions"
                                        :key="option.id"
                                        class="flex-grow-0 card h-100 panel-type-card panel-type-card-available rounded-lg mr-3"
                                    >
                                        <div
                                            class="card-body panel-type-card-body p-2 px-1 rounded-lg"
                                            :class="{
                                                'panel-type-card-active': panel.layout_style == option.value,
                                            }"
                                            @click="panel.layout_style = option.value"
                                        >
                                            <img :src="option.iconPath" :alt="option.alt" height="60" class="mw-100" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <base-form
                                        v-model="panel"
                                        :fields="categoriesPanelFormFields"
                                        :condensed-style="true"
                                    ></base-form>
                                </div>
                            </div>
                            <hr class="my-4" />
                            <p class="h6 mb-1">Step 1: Create Categories</p>
                            <p class="mb-3 small text-secondary">Add categories for items to be dropped into.</p>
                            <div v-for="(category, index) in panel.choice_categories" :key="index" class="mb-1 d-flex">
                                <input
                                    type="text"
                                    class="form-control category-name-input mr-1"
                                    placeholder="Category name"
                                    maxlength="500"
                                    v-model="panel.choice_categories[index].name"
                                />
                                <div class="dropright">
                                    <a
                                        class="btn btn-light px-1"
                                        role="button"
                                        id="dropdownMenuLink"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <span class="icon icon-lg icon-glyphicons-basic-more-vertical"></span>
                                    </a>
                                    <div class="dropdown-menu" aria-labelledby="dropdownMenuLink">
                                        <a
                                            class="dropdown-item"
                                            @click="shiftListItem(index, -1, panel.choice_categories)"
                                        >
                                            <span class="icon icon-sm icon-glyphicons-basic-arrow-up"></span>
                                            Move Up</a
                                        >
                                        <a
                                            class="dropdown-item"
                                            @click="shiftListItem(index, 1, panel.choice_categories)"
                                        >
                                            <span class="icon icon-sm icon-glyphicons-basic-arrow-down"></span>
                                            Move Down</a
                                        >
                                        <a
                                            class="dropdown-item"
                                            @click="removeListItemByIndex(panel.choice_categories, index)"
                                        >
                                            <span class="icon icon-sm icon-glyphicons-basic-bin"></span>
                                            Remove
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-3 pb-2">
                                <button class="btn btn-sm btn-link p-0" @click="createChoiceCategory()">
                                    <span class="icon icon-glyphicons-basic-plus icon-100 align-middle mr-1"></span>
                                    <span class="align-middle">Category</span>
                                </button>
                            </div>
                            <hr class="my-4" />
                            <p class="h6 mb-1">Step 2: Create Items</p>
                            <p class="mb-4 small text-secondary">Add items for learners to categorize.</p>
                            <choice-panel-editor
                                v-model="panel"
                                :choices.sync="panel.choices"
                                :node-options="nodeOptions"
                            ></choice-panel-editor>
                        </div>
                        <!-- Custom Form -->
                        <div v-else-if="panel.panel_type === 'custom_form'">
                            <router-link v-if="customFormEditorRoute" :to="customFormEditorRoute" data-dismiss="modal">
                                <span class="icon icon-glyphicons-basic-square-edit icon-mr"></span>
                                <span>Open Custom Form Builder</span>
                            </router-link>
                        </div>
                        <!-- Review Response Panel -->
                        <div v-else-if="panel.panel_type === 'linked_response'">
                            <p class="mb-1">Select an inquiry from the list below.</p>
                            <p class="small text-secondary mb-3">
                                The learner's response to this inquiry will be displayed when visiting this screen.
                            </p>
                            <v-select
                                v-model="panel.linked_response_panel"
                                :multiple="false"
                                :options="linkablePanels"
                                :close-on-select="true"
                                label="display_name"
                                class="mw-100 mb-3"
                                placeholder="Select an inquiry"
                                :reduce="(option) => option.id"
                            >
                                <template slot="option" slot-scope="option">
                                    <span class="mr-2 align-middle">{{ option.reporting_label || option.header }}</span>
                                    <span class="badge badge-pill badge-light align-middle font-weight-500 mr-2">
                                        <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                                        <span
                                            class="align-middle icon"
                                            :class="[panelTypeIcon(option.panel_type)]"
                                        ></span>
                                    </span>
                                    <span class="text-secondary small align-middle font-weight-500">
                                        <span class="align-middle mr-1">Screen {{ option.node.number }}</span>
                                    </span>
                                </template>
                                <template v-slot:selected-option="option">
                                    <div v-if="option.node">
                                        <span class="mr-2 align-middle">{{option.reporting_label || option.header}}</span>
                                        <span class="badge badge-pill badge-light align-middle font-weight-500 mr-2">
                                            <span class="align-middle mr-1">{{panelTypeLabel(option.panel_type)}}</span>
                                            <span
                                                class="align-middle icon"
                                                :class="[panelTypeIcon(option.panel_type)]"
                                            ></span>
                                        </span>
                                        <span class="text-secondary small align-middle font-weight-500">
                                            <span class="align-middle mr-1">Screen {{ option.node.number }}</span>
                                        </span>
                                    </div>
                                </template>
                                <template slot="no-options">
                                    <span class="text-secondary">No inquiries to display.</span>
                                </template>
                            </v-select>
                        </div>
                        <!-- Notepad Panel -->
                        <div v-else-if="panel.panel_type === 'notepad'">
                            <base-form
                                v-model="panel"
                                :fields="notepadPanelFormFields"
                                :inline-style="true"
                            ></base-form>
                            <div class="text-secondary pt-4 pb-3" v-if="caseID">
                                <div class="mt-1 pl-2 text-secondary medium">
                                    <span class="icon icon-glyphicons-basic-switch-off mr-1"></span>
                                    <span>
                                        The notepad toolbar button is currently
                                        <router-link :to="experienceSettingsRouteObj">
                                            <span v-show="caseObj.global_notepad_enabled">enabled</span>
                                            <span v-show="!caseObj.global_notepad_enabled">turned off</span>
                                        </router-link>
                                        for this learning experience.
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!-- 360 Video Panel -->
                        <div v-else-if="panel.panel_type === '3d_video'">
                            <rich-content-editor
                                v-model="panel.elements"
                                :allowed-element-types="['vr']"
                                :allowed-video-fields="[
                                    'loop_media',
                                    'play_video_before_continue',
                                    'continue_after_video_finished',
                                ]"
                                :show-info-footers="false"
                                :max-items="1"
                                class="my-4"
                            ></rich-content-editor>
                        </div>
                        <!-- Vitals Panel -->
                        <div v-else-if="panel.panel_type === 'vitals'">
                            <p class="h6 mb-2">Configure which vital signs to display:</p>
                            <p class="text-secondary small">
                                Choose a code and a color for each vital sign you want to simulate.
                            </p>
                            <!-- Vitals Series Config -->
                            <div v-if="panel.vitals">
                                <div class="row mb-4">
                                    <div class="col-12">
                                        <div v-for="(series, index) in activeVitalsSeries" :key="series.id">
                                            <div class="d-flex align-items-center mb-2">
                                                <div class="mr-3">
                                                    <rich-text-vitals
                                                        placeholder="Vitals Symbol (e.g. HR)"
                                                        maxlength="20"
                                                        v-model="series.code"
                                                    ></rich-text-vitals>
                                                </div>
                                                <div class="mr-3">
                                                    <div class="dropdown">
                                                        <button
                                                            class="btn btn-white px-1"
                                                            type="button"
                                                            id="vitalsColorDropdown"
                                                            data-toggle="dropdown"
                                                            aria-haspopup="true"
                                                            aria-expanded="false"
                                                        >
                                                            <div class="d-flex align-items-center">
                                                                <div
                                                                    class="rounded vitals-color-preview mr-2"
                                                                    :style="{ 'background-color': series.hex_color }"
                                                                ></div>
                                                                <div>
                                                                    <span
                                                                        class="icon icon-xs icon-glyphicons-basic-chevron-down text-secondary"
                                                                    ></span>
                                                                </div>
                                                            </div>
                                                        </button>
                                                        <div
                                                            class="dropdown-menu dropdown-menu-right vitals-color-dropdown"
                                                            aria-labelledby="vitalsColorDropdown"
                                                        >
                                                            <a
                                                                v-for="color in vitalsColorOptions"
                                                                :key="color"
                                                                class="dropdown-item"
                                                                @click="setVitalsColor(series, color)"
                                                            >
                                                                <div
                                                                    class="rounded vitals-color-preview"
                                                                    :style="{ 'background-color': color }"
                                                                ></div>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="dropright">
                                                    <a
                                                        class="btn btn-light btn-sm px-1"
                                                        role="button"
                                                        id="dropdownMenuLink"
                                                        data-toggle="dropdown"
                                                        aria-haspopup="true"
                                                        aria-expanded="false"
                                                    >
                                                        <span
                                                            class="icon icon-lg icon-glyphicons-basic-more-vertical"
                                                        ></span>
                                                    </a>
                                                    <div class="dropdown-menu" aria-labelledby="dropdownMenuLink">
                                                        <a
                                                            class="dropdown-item"
                                                            @click="shiftVitalsSeriesPosition(index, -1)"
                                                        >
                                                            <span
                                                                class="icon icon-sm icon-glyphicons-basic-arrow-up"
                                                            ></span>
                                                            Move Up</a
                                                        >
                                                        <a
                                                            class="dropdown-item"
                                                            @click="shiftVitalsSeriesPosition(index, 1)"
                                                        >
                                                            <span
                                                                class="icon icon-sm icon-glyphicons-basic-arrow-down"
                                                            ></span>
                                                            Move Down</a
                                                        >
                                                        <a class="dropdown-item" @click="removeVitalsSeries(series)">
                                                            <span class="icon icon-sm icon-glyphicons-basic-bin"></span>
                                                            Remove
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <button class="btn btn-sm btn-light mt-2" @click="createVitalsSeries()">
                                                <span class="icon icon-glyphicons-basic-plus"></span>
                                                New
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <br />
                                <p class="h6 mb-2">Configure Vitals Data</p>
                                <p class="text-secondary small mb-4">
                                    Either choose a high and low boundary for each vitals series, or upload a csv
                                    containing a series of data points for each vital sign.
                                </p>
                                <div class="row mb-4">
                                    <div class="col-12">
                                        <div class="custom-control custom-radio custom-control-inline mb-1">
                                            <input
                                                type="radio"
                                                id="vitals-boundaries"
                                                name="vitals-boundaries"
                                                class="custom-control-input"
                                                v-model="panel.vitals.use_uploaded_data"
                                                :value="false"
                                            />
                                            <label class="custom-control-label" for="vitals-boundaries"
                                                >Set Boundaries</label
                                            >
                                        </div>
                                        <div class="custom-control custom-radio custom-control-inline mb-2">
                                            <input
                                                type="radio"
                                                id="vitals-upload"
                                                name="vitals-upload"
                                                class="custom-control-input"
                                                v-model="panel.vitals.use_uploaded_data"
                                                :value="true"
                                            />
                                            <label class="custom-control-label" for="vitals-upload"> Upload CSV</label>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="!panel.vitals.use_uploaded_data">
                                    <div
                                        v-for="series in activeVitalsSeries"
                                        :key="series.id"
                                        class="card bg-light p-2 px-3 mb-2"
                                    >
                                        <div class="d-flex align-items-start mb-1">
                                            <div class="flex-grow-0">
                                                <div
                                                    class="pt-1 vitals-code vitals-boundary-code small font-weight-500"
                                                    v-html="series.code"
                                                ></div>
                                            </div>
                                            <div class="flex-grow-1">
                                                <div class="d-flex align-items-center mb-1">
                                                    <div class="vitals-glyph-spacer flex-grow-0 flex-shrink-0"></div>
                                                    <div class="mr-2">
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm vitals-boundary-input"
                                                            placeholder="Lowest"
                                                            v-model="series.low_boundary"
                                                        />
                                                    </div>
                                                    <div class="mx-1">to</div>
                                                    <div class="mx-2">
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm vitals-boundary-input"
                                                            placeholder="Highest"
                                                            v-model="series.high_boundary"
                                                        />
                                                    </div>
                                                    <div class="ml-3">
                                                        <div class="custom-control custom-checkbox">
                                                            <input
                                                                v-model="series.show_decimals"
                                                                type="checkbox"
                                                                class="custom-control-input"
                                                                :id="'show-decimals-' + series.id"
                                                            />
                                                            <label
                                                                class="custom-control-label"
                                                                :for="'show-decimals-' + series.id"
                                                                >Show Decimals</label
                                                            >
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="mb-1">
                                                    <div
                                                        v-for="(trigger, index) in series.triggers"
                                                        :key="index"
                                                        class="mt-1"
                                                    >
                                                        <div class="d-flex flex-wrap flex-lg-nowrap align-items-center">
                                                            <div class="vitals-glyph-spacer flex-grow-0 flex-shrink-0">
                                                                <span
                                                                    class="icon icon-glyphicons-basic-flash text-secondary icon-xs"
                                                                ></span>
                                                            </div>
                                                            <div class="flex-grow-0 flex-shrink-0 mr-1">
                                                                <select
                                                                    v-model="trigger.change_type"
                                                                    class="form-control form-control-sm"
                                                                >
                                                                    <option value="replace_with">
                                                                        Change limits to
                                                                    </option>
                                                                    <option value="increase_by">
                                                                        Change limits by
                                                                    </option>
                                                                </select>
                                                            </div>
                                                            <!-- Change-To Settings -->
                                                            <div
                                                                v-show="trigger.change_type == 'replace_with'"
                                                                class="flex-grow-0 mx-2"
                                                            >
                                                                <div class="d-flex align-items-center">
                                                                    <div class="flex-grow-0">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Lowest"
                                                                            class="form-control form-control-sm vitals-boundary-input"
                                                                            v-model="trigger.low_boundary"
                                                                        />
                                                                    </div>
                                                                    <div class="flex-grow-0 mx-2">to</div>
                                                                    <div class="flex-grow-0">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Highest"
                                                                            class="form-control form-control-sm vitals-boundary-input"
                                                                            v-model="trigger.high_boundary"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <!-- Change-By Settings -->
                                                            <div
                                                                v-show="trigger.change_type == 'increase_by'"
                                                                class="flex-grow-0 mx-2"
                                                            >
                                                                <input
                                                                    type="text"
                                                                    placeholder="Value"
                                                                    class="form-control form-control-sm vitals-boundary-input"
                                                                    v-model="trigger.increase_value"
                                                                />
                                                            </div>
                                                            <div class="flex-grow-0 mr-2">
                                                                <select
                                                                    v-model="trigger.trigger_type"
                                                                    class="form-control form-control-sm"
                                                                >
                                                                    <option value="choice">when</option>
                                                                    <option value="time_delay">after delay of</option>
                                                                </select>
                                                            </div>
                                                            <div
                                                                v-show="trigger.trigger_type == 'choice'"
                                                                class="flex-grow-0 mr-2"
                                                            >
                                                                <div class="d-flex align-items-center">
                                                                    <div class="flex-grow-0 mr-2">
                                                                        <select
                                                                            class="form-control form-control-sm"
                                                                            v-model="trigger.choice_id"
                                                                        >
                                                                            <option
                                                                                v-for="choice in panelChoiceInquiries"
                                                                                :key="choice.id"
                                                                                :value="choice.id"
                                                                            >
                                                                                {{ choice.text }}
                                                                            </option>
                                                                        </select>
                                                                    </div>
                                                                    <div class="flex-grow-0 flex-shrink-0 small">
                                                                        is selected
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div v-show="trigger.trigger_type == 'time_delay'">
                                                                <div class="d-flex align-items-center">
                                                                    <div class="flex-grow-0 mr-2">
                                                                        <input
                                                                            type="number"
                                                                            placeholder="Value"
                                                                            class="form-control form-control-sm vitals-boundary-input"
                                                                            v-model="trigger.time_delay_seconds"
                                                                        />
                                                                    </div>
                                                                    <div class="flex-grow-0 small">seconds</div>
                                                                </div>
                                                            </div>
                                                            <div class="my-1">
                                                                <span
                                                                    class="icon icon-glyphicons-basic-times icon-btn icon-sm text-secondary"
                                                                    @click="removeTrigger(series, index)"
                                                                ></span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="small d-flex">
                                                    <div class="vitals-glyph-spacer"></div>
                                                    <button class="btn btn-gray badge" @click="addTrigger(series)">
                                                        + Trigger
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="panel.vitals.use_uploaded_data">
                                    <p class="mt-2 mb-0">Uploading a CSV</p>
                                    <ul class="mb-4">
                                        <li>If you're starting from scratch, download the template file.</li>
                                        <li>
                                            Each data series will be matched to its vital sign based on the
                                            <strong>order</strong> it appears in the CSV (not by column headers).
                                        </li>
                                        <li>Each row of data represents 800ms of the vitals display.</li>
                                        <li>All columns must contain the same number of rows.</li>
                                    </ul>
                                    <!-- Download Template .csv File -->
                                    <div class="mb-1">
                                        <form method="GET" :action="'/api/vitals/template/'">
                                            <input
                                                type="text"
                                                style="display: none"
                                                name="seriesCodes"
                                                :value="vitalsSeriesCodesSerialized"
                                            />
                                            <button class="btn btn-light btn-sm" type="submit">
                                                <span
                                                    class="icon icon-lg icon-glyphicons-basic-cloud-download mr-2"
                                                ></span
                                                >Download Template File
                                            </button>
                                        </form>
                                    </div>
                                    <br />
                                    <!-- Uploaded Vitals -->
                                    <div v-if="panel.vitals">
                                        <file-upload
                                            v-model="panel.vitals.csv_file"
                                            :replaceable="true"
                                            placeholder="Drop a CSV file here"
                                            accept=".csv"
                                        ></file-upload>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Results Panel -->
                        <div v-else-if="panel.panel_type === 'score'">
                            <base-form
                                v-model="panel"
                                :fields="scorePanelFormFields"
                                :inline-style="true"
                                :condensed-style="true"
                            ></base-form>
                            <div class="mt-3" v-show="freeTierActive">
                                <p class="text-secondary small mb-0">
                                    <span>
                                        Note: Learner Results PDF downloads are only available on paid plans.
                                    </span>
                                </p>
                            </div>
                        </div>
                        <!-- Discovery Panel -->
                        <div v-else-if="panel.panel_type === 'discovery'">
                            <base-form v-model="panel" :fields="discoveryPanelFormFields" :inline-style="true"></base-form>
                            <div class="mb-2">Show discovered responses for:</div>
                            <v-select
                                v-model="panel.linked_response_panel"
                                :multiple="false"
                                :options="discoveryCompatiblePanels"
                                :close-on-select="true"
                                label="header"
                                class="mw-100 mb-3"
                                placeholder="Select an inquiry"
                                :reduce="(option) => option.id"
                            >
                                <template slot="option" slot-scope="option">
                                    <span class="align-middle">{{ option.reporting_label || option.header }}</span>
                                    <span class="badge badge-pill badge-light align-middle font-weight-500 ml-1">
                                        <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                                        <span class="align-middle icon" :class="[panelTypeIcon(option.panel_type)]"></span>
                                    </span>
                                </template>
                                <template v-slot:selected-option="option">
                                    <span class="align-middle">{{ option.reporting_label || option.header }}</span>
                                    <span class="badge badge-pill badge-light align-middle font-weight-500 ml-1">
                                        <span class="align-middle mr-1">{{ panelTypeLabel(option.panel_type) }}</span>
                                        <span class="align-middle icon" :class="[panelTypeIcon(option.panel_type)]"></span>
                                    </span>
                                </template>
                                <template slot="no-options">
                                    <span class="text-secondary">No inquiries to display.</span>
                                </template>
                            </v-select>
                        </div>
                    </div>

                    <!-- More Settings Tab -->
                    <div v-show="currentTab === 'settings'">
                        <!-- Custom/Display Panel -->
                        <div v-if="panel.panel_type === 'custom'">
                            <base-form v-model="panel" :fields="customPanelFormFields" :inline-style="true"></base-form>
                        </div>
                        <!-- Buttons Panel -->
                        <div v-else-if="panel.panel_type === 'multichoice'">
                            <base-form
                                v-model="panel"
                                :fields="buttonsPanelFormFields"
                                :inline-style="true"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                        <!-- Text Response Panel -->
                        <div v-else-if="panel.panel_type === 'textarea'">
                            <base-form v-model="panel" :fields="textResponseFormFields" :indent-columns="6"></base-form>
                            <div v-if="panel.analyze_text_responses">
                                <div
                                    v-for="(textEntity, index) in panel.text_entities"
                                    :key="index"
                                    class="card bg-light pt-3 pb-3 px-3 mb-3"
                                >
                                    <div class="d-flex">
                                        <div class="flex-grow-1">
                                            <base-form
                                                v-model="panel.text_entities[index]"
                                                :fields="textEntityFormFields"
                                                :inline-style="true"
                                            ></base-form>
                                            <div class="mb-3 d-flex align-items-center">
                                                <span class="mr-2">Valid Phrase/Keyword Variations</span>
                                                <span class="badge badge-gray">{{ textEntity.keywords.length }}</span>
                                            </div>
                                            <div class="d-flex align-items-center flex-wrap">
                                                <div
                                                    class="flex-grow-0 mr-3 mb-2"
                                                    v-for="(keyword, index) in textEntity.keywords"
                                                    :key="index"
                                                >
                                                    <div class="d-flex align-items-center">
                                                        <div class="flex-grow-0" style="margin-right: 2px">
                                                            <input
                                                                type="text"
                                                                class="form-control"
                                                                placeholder="Phrase or keyword"
                                                                v-model="textEntity.keywords[index].value"
                                                            />
                                                        </div>
                                                        <div class="flex-grow-0">
                                                            <button
                                                                class="btn btn-transparent btn-sm p-1"
                                                                @click="removeTextEntityKeyword(textEntity, index)"
                                                            >
                                                                <span class="icon icon-glyphicons-basic-times"></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="flex-grow-0 mb-2">
                                                    <button
                                                        class="btn btn-sm btn-gray"
                                                        @click="createKeywordVariation(textEntity)"
                                                    >
                                                        <span class="icon icon-glyphicons-basic-plus"></span>
                                                        Variation
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex-grow-0 d-flex flex-column align-items-center ml-3">
                                            <button
                                                class="btn btn-transparent py-0 px-1 mt-1 mb-2"
                                                @click="removeTextEntity(panel, index)"
                                            >
                                                <span class="icon icon-glyphicons-basic-bin"></span>
                                            </button>
                                            <div class="mb-3">
                                                <div class="btn-group-vertical">
                                                    <button
                                                        type="button"
                                                        class="btn btn-transparent btn-sm py-0 px-1"
                                                        @click="shiftListItem(index, -1, panel.text_entities)"
                                                    >
                                                        <span class="icon icon-glyphicons-basic-arrow-up"></span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        class="btn btn-transparent btn-sm py-0 px-1"
                                                        @click="shiftListItem(index, 1, panel.text_entities)"
                                                    >
                                                        <span class="icon icon-glyphicons-basic-arrow-down"></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <button class="btn btn-sm btn-light" @click="createTextEntity(panel)">
                                        <span class="icon icon-glyphicons-basic-plus"></span>
                                        Key Info
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- Custom GPT Panel -->
                        <div v-else-if="panel.panel_type === 'chat_v2'">
                            <base-form
                                v-model="panel"
                                :fields="customGPTFormFields"
                                :inline-style="true"
                            ></base-form>
                            <base-form
                                v-model="panel.ai_analysis_config"
                                :fields="customGPTFormFieldsPart2"
                                :inline-style="true"
                            ></base-form>
                        </div>
                        <!-- Voice Conversation Panel -->
                        <div v-else-if="panel.panel_type === 'voice_conversation'">
                            <base-form
                                v-model="panel.ai_analysis_config"
                                :fields="voiceConversationPanelFields"
                                :inline-style="true"
                            ></base-form>
                            <base-form
                                v-model="panel"
                                :fields="voiceConversationPanelExtraFields"
                                :inline-style="true"
                            ></base-form>
                        </div>
                        <!-- Voice Recording Panel -->
                        <div v-else-if="panel.panel_type === 'voice_recording'">
                            <base-form
                                v-model="panel"
                                :fields="voiceRecordingPanelFields"
                                :inline-style="true"
                                class="mb-5"
                            ></base-form>
                        </div>
                        <!-- Image Hotspots Panel -->
                        <div v-else-if="panel.panel_type === 'image_hotspots'">
                            <base-form
                                v-model="panel"
                                :fields="imageHotspotsFormFieldsExtra"
                                :inline-style="false"
                                :condensed-style="false"
                            ></base-form>
                        </div>
                        <!-- Multiple Selection Panel -->
                        <div v-else-if="panel.panel_type === 'multiselect'">
                            <base-form
                                v-model="panel"
                                :fields="selectionPanelFormFields"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                        <!-- Single Selection Panel -->
                        <div v-else-if="panel.panel_type === 'singleselect'">
                            <base-form
                                v-model="panel"
                                :fields="selectionPanelFormFields"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                        <!-- Step Order Panel -->
                        <div v-else-if="panel.panel_type === 'step_order'">
                            <base-form
                                v-model="panel"
                                :fields="stepOrderPanelFormFields"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                        <!-- Categories -->
                        <div v-else-if="panel.panel_type === 'categories'">
                            <base-form
                                v-model="panel"
                                :fields="categoriesPanelFormFieldsExtra"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                        <!-- Custom Form -->
                        <div v-else-if="panel.panel_type === 'custom_form'">
                            <base-form
                                v-model="panel"
                                :fields="customFormPanelFields"
                                :condensed-style="true"
                            ></base-form>
                        </div>
                        <!-- 360 Video Panel -->
                        <div v-else-if="panel.panel_type === '3d_video'">
                            <base-form v-model="panel" :fields="threeDVideoFormFields"></base-form>
                        </div>
                    </div>

                    <!-- Reporting Tab -->
                    <div v-show="currentTab === 'reporting'">
                        <base-form
                            v-model="panel"
                            :fields="reportingFields"
                            :inline-style="true"
                        ></base-form>
                    </div>
                </div>
            </template>
            <!-- Footer Buttons -->
            <template slot="footer">
                <button @click="openDeletePanel()" :disabled="editLocked || modalLoading" type="button" class="btn btn-light btn-sm">
                    <span class="icon icon-100  icon icon-glyphicons-basic-bin mr-1 align-middle"></span>
                    <span class="align-middle">Delete</span>
                </button>
                <button @click="openClone()" :disabled="modalLoading" type="button" class="btn btn-light btn-sm">
                    <span class="icon icon-100  icon-glyphicons-basic-squares mr-1 align-middle"></span>
                    <span class="align-middle">Clone</span>
                </button>
                <info-popover
                    placement="top"
                    content="Change content-type"
                    :delay="500"
                >
                    <button
                        @click="openChangePanelTypeModal()"
                        type="button"
                        class="btn btn-light btn-sm"
                        :disabled="editLocked || modalLoading"
                    >
                        <span class="icon icon-100 icon-glyphicons-basic-random mr-1 align-middle"></span>
                        <span class="align-middle">Type</span>
                    </button>
                </info-popover> 
                <button class="btn btn-light btn-sm" type="button" data-dismiss="modal">
                    <span class="align-middle">Cancel</span>
                </button>
                <button
                    @click="savePanel()"
                    :disabled="editLocked || modalLoading"
                    class="btn btn-primary btn-sm"
                    type="button"
                >
                    <span class="align-middle">Save</span>
                </button>
            </template>
        </base-modal>

        <!-- Clone Panel Modal -->
        <base-modal id="clone-panel-modal" :loading="modalLoading" size="medium" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-squares mr-1 align-middle"></span>
                <span class="align-middle">Clone Content</span>
            </template>
            <template>
                <p class="mb-2 pb-1">Clone content into:</p>
                <div class="mb-2">
                    <div class="custom-control custom-radio mb-2">
                        <input
                            v-model="clonePanelInsideNode"
                            :value="true"
                            type="radio"
                            :id="panel.id + 'clone-panel-internal'"
                            class="custom-control-input"
                            :disabled="editLocked"
                        />
                        <label class="custom-control-label" :for="panel.id + 'clone-panel-internal'">This screen</label>
                        <p class="mb-0 text-secondary small">Clone content into the screen you're currently viewing.</p>
                    </div>
                    <div class="custom-control custom-radio">
                        <input
                            v-model="clonePanelInsideNode"
                            :value="false"
                            type="radio"
                            :id="panel.id + 'clone-panel-external'"
                            class="custom-control-input"
                        />
                        <label class="custom-control-label" :for="panel.id + 'clone-panel-external'"
                            >A different screen</label
                        >
                        <p class="mb-0 text-secondary small">Clone content into a different screen or learning experience.</p>
                    </div>
                </div>
                <div v-show="!clonePanelInsideNode" class="mt-3 ml-sm-1">
                    <div class="row mb-3">
                        <div class="col-12">
                            <div class="col-12 col-form-label">Learning experience:</div>
                            <div class="col-12">
                                <library-item-selector
                                    v-model="cloneForm.libraryItem"
                                    :item-types="['owned_case']"
                                    placeholder="Select a learning experience..."
                                    emptyText="No learning experiences found matching your search."
                                    ref="itemSelector"
                                ></library-item-selector>
                            </div>
                        </div>
                    </div>
                    <div>
                        <base-form
                            v-model="cloneForm"
                            :fields="clonePanelFields"
                            ref="clonePanelForm"
                        ></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-light btn-sm" type="button" data-dismiss="modal">Cancel</button>
                <button
                    @click="clonePanel()"
                    :disabled="modalLoading || !clonePanelFormValid"
                    class="btn btn-primary btn-sm"
                    type="button"
                >
                    Clone
                </button>
            </template>
        </base-modal>

        <!-- Delete Panel Modal -->
        <base-modal id="delete-panel-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-bin mr-1 align-middle"></span>
                <span class=align-middle>Delete - {{ panelTypeLabel(panel.panel_type) }}</span>
            </template>
            <template>
                <p>Remove this content from the screen?</p>
            </template>
            <template slot="footer">
                <button class="btn btn-light btn-sm" type="button"  data-dismiss="modal">Cancel</button>
                <button @click="deletePanel()" class="btn btn-danger btn-sm" type="button">Delete</button>
            </template>
        </base-modal>

        <!-- Global Case Styles -->
        <global-style-block v-if="caseObj" :case-settings="caseObj"></global-style-block>

        <!-- Panel Creator -->
        <panel-type-selector
            ref="panelTypeSelector"
            v-on:selected="panelTypeSelectedCb($event)"
            v-on:cancelled="panelTypeChangeCancelledCb()"
        >
            <template slot="header">
                <p class="mb-0 text-secondary">Choose a content type to switch to:</p>
            </template>
        </panel-type-selector>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import FileUpload from "@/components/FileUpload"
import RichTextVitals from "@/components/RichTextVitals"
import GlobalStyleBlock from "@/components/GlobalStyleBlock"
import ChoicePanelEditor from "@/components/ChoicePanelEditor"
import RichContentEditor from "@/components/RichContentEditor"
import LibraryItemSelector from "@/components/LibraryItemSelector"
import AiBadge from "@/components/AiBadge"
import InfoPopover from "@/components/InfoPopover"
import TextareaAutoExpand from "@/components/TextareaAutoExpand"
import PanelTypeSelector from "@/components/PanelTypeSelector"
import { panelTypeIcons, panelTypeLabels, panelTypes, aiVoiceNames, languageCodes } from "@/assets/js/settings.js"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "PanelEditor",
    props: {
        caseObj: {
            type: Object,
            required: false,
            default() {
                return {}
            },
        },
        node: {
            type: Object,
            required: false,
            default() {
                return {
                    panels: [],
                }
            },
        },
        preserveMap: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        BaseForm,
        BaseModal,
        FileUpload,
        GlobalStyleBlock,
        ChoicePanelEditor,
        RichTextVitals,
        RichContentEditor,
        LibraryItemSelector,
        AiBadge,
        InfoPopover,
        TextareaAutoExpand,
        PanelTypeSelector,
    },
    data() {
        return {
            panel: {
                id: null,
                vitals: {
                    series: [],
                },
                elements: [],
                choice_categories: [],
                text_entities: [],
                ai_analysis_config: {},
            },
            currentTab: "content",
            savedPanel: {},
            modalLoading: false,
            referenceDocumentUpload: null,
            referenceDocumentUploading: false,
            referenceDocumentStatusTimeoutIds: [],
            textResponsePanels: [],
            linkablePanels: [],
            cloneNodeOptions: [],
            cloneNodeOptionsLoading: false,
            panelIsEdited: false,
            showKeyInteractions: false,
            clonePanelInsideNode: true,
            aiVoiceNames: aiVoiceNames,
            vitalsColorOptions: [
                "#f93446",
                "#3fcad0",
                "#37a9fd",
                "#f9c912",
                "#eff1f3",
                "#42d264",
                "#fd7e14",
                "#e53df2",
            ],
            cloneForm: {
                libraryItem: null,
                nodeId: null,
            },
            preContentVideoFields: [
                {
                    name: "Show a video first",
                    key: "show_video_first",
                    type: "checkbox",
                    description: "Show a video before displaying this content.",
                },
            ],
            headerFields: [
                {
                    name: "",
                    key: "sub_header",
                    required: false,
                    type: "richtext",
                    rows: 3,
                    placeholder: "Add a header here...",
                    defaultContent: "<h2></h2>"
                },
            ],
            reportingFields: [
                {
                    name: "Reporting Label",
                    key: "label",
                    required: false,
                    subtext: "An optional label used to identify this activity in reports.",
                },
                {
                    name: "Exclude this activity from score & summary",
                    type: "checkbox",
                    key: "exclude_from_total_score",
                    positionIndent: true,
                    subtext: `
                        When checked, this activity will be excluded from the learner's total score and learner response summary.
                    `,
                },
            ],
            buttonsPanelFormFields: [
                {
                    name: "Disable buttons after they're pressed",
                    key: "disable_items_after_selection",
                    type: "checkbox",
                    subtext: "When checked, buttons will be disabled after they're pressed for the first time.",
                },
                {
                    name: "Only score the learner's first response",
                    key: "score_first_response_only",
                    type: "checkbox",
                    subtext: "When checked, only the learner's first selection will count toward their overall score.",
                },
            ],
            scorePanelFormFields: [
                {
                    name: "Display the learner's total score",
                    key: "display_score",
                    type: "checkbox",
                },
                {
                    name: "Display a breakdown of the learner's results",
                    key: "display_breakdown",
                    type: "checkbox",
                    description: `
                        Display a report containing the learner's responses during their
                        session, with the ability to download as a PDF.
                    `,
                },
                {
                    name: "Display pass/fail status",
                    key: "display_pass_fail",
                    type: "checkbox",
                    description: `
                        Display whether the learner was successful or unsuccessful during their session.
                    `,
                },
                {
                    name: "Include Notepad notes",
                    key: "include_learner_notes",
                    type: "checkbox",
                    description: `
                        When checked, the learner's entries in the Notepad content-type will be included in the results summary.
                    `,
                },
            ],
            notepadPanelFormFields: [
                {
                    name: "Notepad Placeholder",
                    key: "textarea_placeholder",
                    placeholder: 'e.g. "Your Notes Here"',
                },
                {
                    name: "Notepad Rows",
                    key: "textarea_rows",
                    type: "number",
                    placeholder: "The minimum number of rows to display for the notepad (optional)",
                },
            ],
            customPanelFormFields: [
                {
                    name: "Show horizontal dividers between elements",
                    key: "display_hr_dividers",
                    type: "checkbox",
                },
            ],
            discoveryPanelFormFields: [
                {
                    name: "Include neutral & incorrect items",
                    subtext: "When checked, neutral and incorrect items will be displayed in the discovered items list.",
                    key: "include_non_correct_items",
                    type: "checkbox",
                },
            ],
            categoriesLayoutOptions: [
                {
                    value: "vertical",
                    iconPath: "/static/general/img/icons/categories-vertical.svg",
                    alt: "Vertical layout",
                },
                {
                    value: "horizontal",
                    iconPath: "/static/general/img/icons/categories-horizontal.svg",
                    alt: "Horizontal layout",
                },
            ],
            tabsLayoutOptions: [
                {
                    value: "default",
                    iconPath: "/static/general/img/icons/tabs-folder.svg",
                    name: "Folder-style",
                    alt: "Default tabs style",
                },
                {
                    value: "accordion",
                    iconPath: "/static/general/img/icons/tabs-accordion.svg",
                    name: "Accordion-style",
                    alt: "Accordion style",
                },
            ],
            aiAnalysisFields: [
                {
                    name: "Consider the full learning experience when generating feedback",
                    subtext: "Use content from other screens in this learning experience to generate more relevant feedback.",
                    type: "checkbox",
                    key: "full_context_enabled",
                    badge: "Beta",
                    badgeClass: "badge-highlight",
                    badgeIconClass: "icon-glyphicons-basic-chemistry-alt",
                },
            ],
            aiAnalysisScoreFields: [
                {
                    name: "Enable Scoring",
                    type: "checkbox",
                    key: "enable_ai_scoring",
                    condensedStyle: true,
                },
            ],
            imageHotspotsFormFields:[
                {
                    name: "",
                    type: "radio",
                    key: "hotspots_content_type",
                    options: [
                        {
                            value: "2d_image",
                            label: "2D Image",
                        },
                        {
                            value: "360_image",
                            label: "360° Image",
                        },
                    ],
                    inlineStyle: true,
                    positionIndent: true,
                },
            ],
            textEntityFormFields: [
                {
                    name: "Display Label",
                    key: "display_label",
                    type: "text",
                    description: "A label to identify this piece of information.",
                },
                {
                    name: "Score",
                    key: "score",
                    type: "number",
                    description: "This score will only be applied if this piece of information is matched.",
                },
                {
                    name: "Correct/Incorrect",
                    key: "correct",
                    type: "select",
                    options: [
                        {
                            label: "Correct (Should Be Included)",
                            value: true,
                        },
                        {
                            label: "Incorrect (Should Not Be Included)",
                            value: false,
                        },
                    ],
                },
                {
                    name: "Hint Message",
                    key: "feedback_message",
                    type: "textarea",
                    rows: 2,
                    description: "A feedback message to show the learner if they omit this information.",
                },
            ],
            customGPTFormFields: [
                {
                    name: "Who starts the conversation?",
                    key: "conversation_start_mode",
                    type: "radio",
                    options: [
                        { value: "learner", label: "🗣️ Learner Starts" },
                        {
                            value: "ai",
                            label: "🤖 AI Starts",
                            badge: "NEW",
                            badgeClass: "badge-highlight"
                        },
                    ],
                },
                {
                    name: "Allow learners to continue chatting when returning to this screen",
                    key: "allow_answer_changing",
                    type: "switch",
                    positionIndent: true,
                },
            ],
        }
    },
    computed: {
        // Controls whether to show the 'extra settings' tab in the editor.
        selectedPanelHasExtraSettings() {
            return [
                "custom",
                "multichoice",
                "textarea",
                "chat_v2",
                "voice_conversation",
                "multiselect",
                "singleselect",
                "step_order",
                "image_hotspots",
                "categories",
                "voice_recording",
                "custom_form",
                "3d_video",
            ].includes(this.panel.panel_type)
        },
        // Controls whether to show the reporting tab in the editor.
        selectedPanelHasReporting() {
            return [
                "multichoice",
                "textarea",
                "multiselect",
                "singleselect",
                "step_order",
                "chat",
                "chat_v2",
                "image_hotspots",
                "categories",
                "voice_recording",
                "custom_form",
                "voice_conversation",
            ].includes(this.panel.panel_type)
        },
        // Controls whether to show the pre-content video form in the editor.
        selectedPanelAllowsPreContentVideo() {
            return [
                "text",
                "image",
                "audio",
                "pdf",
                "custom",
                "tabs",
                "multichoice",
                "textarea",
                "multiselect",
                "singleselect",
                "step_order",
                "linked_response",
                "chat",
                "categories",
                "voice_recording",
                "voice_conversation",
                "custom_form",
                "notepad",
            ].includes(this.panel.panel_type)
        },
        // Controls whether to show the header field in the editor.
        selectedPanelAllowsHeader() {
            return [
                "tabs",
                "multichoice",
                "textarea",
                "multiselect",
                "singleselect",
                "step_order",
                "linked_response",
                "score",
                "chat",
                "categories",
                "voice_recording",
                "custom_form",
                "image_hotspots",
                "notepad",
                "3d_video",
                "discovery",
            ].includes(this.panel.panel_type)
        },
        // The ID of the current case being edited.
        caseID() {
            if (!this.caseObj) {
                return null
            }
            return this.caseObj.id
        },
        // The ID of the current node being edited.
        nodeID() {
            if (!this.node) {
                return null
            }
            return this.node.id
        },
        userDetails() {
            return this.$store.getters.user
        },
        // The ID the user's organization.
        currentOrganizationId() {
            return this.$store.getters.currentOrganizationId
        },
        // A flattened list of all the panels contained in the current node.
        panelsFlat() {
            if (!this.node.columns) {
                return []
            }
            return this.node.columns.reduce((acc, column) => acc.concat(column.panels), [])
        },
        // Returns true if editing should be disabled.
        editLocked() {
            return this.$store.getters.caseIsReadOnly
        },
        // Returns true if the editor modal is currently open.
        editModalIsOpen() {
            return this.$store.getters.editPanelModalIsOpen
        },
        // Returns a route to the settings view for the current case.
        experienceSettingsRouteObj() {
            return {
                name: "CaseSettings",
                params: { id: this.caseID },
                query: { view: "experience" },
            }
        },
        // Returns true if the current organization is on the free tier.
        freeTierActive() {
            return this.$store.getters.freeTierActive
        },
        // Returns a list of all nodes in the current case, with the node currently being
        // viewed removed.
        nodeOptions() {
            let allNodes = [...this.$store.getters.nodeOptions]
            // Remove the node that's currently being viewed.
            let index = allNodes.findIndex((n) => n.id === this.nodeID)
            allNodes.splice(index, 1)
            return allNodes
        },
        // The case ID to be cloned into using the clone form.
        cloneCaseId() {
            return this.cloneForm.libraryItem ? this.cloneForm.libraryItem.case.id : null
        },
        // Returns true if the current panel has elements.
        panelHasElements() {
            return this.panel.elements && this.panel.elements.length > 0
        },
        // A flat list of text response panels.
        textResponsePanelsFlat() {
            return this.textResponsePanels.map((panel) => {
                return {
                    id: panel.id,
                    title: panel.display_name,
                }
            })
        },
        // A list of panels that can be used with discovery panels.
        discoveryCompatiblePanels() {
            return this.panelsFlat.filter((p) => {
                return [
                    "multichoice",
                    "multiselect",
                    "singleselect",
                    "step_order",
                    "image_hotspots",
                    "categories",
                    "custom_form",
                    "chat",
                    "chat_v2",
                ].includes(p.panel_type)
            })
        },
        panelChoiceInquiries() {
            let choiceInquiries = this.panelsFlat
                .filter((p) => ["multiselect", "singleselect", "image_hotspots"].includes(p.panel_type))
                .map((p) => p.choices)
                .flat()
            return choiceInquiries
        },
        clonePanelFields() {
            let vm = this
            return [
                {
                    name: "Screen:",
                    key: "nodeId",
                    required: true,
                    type: "search",
                    options: vm.cloneNodeOptions,
                    loading: vm.cloneNodeOptionsLoading,
                    placeholder: "Select a screen to clone content into",
                    labelField: "label",
                    valueField: "id",
                    searchFields: ["label"],
                    maxItems: 1,
                },
            ]
        },
        clonePanelFormValid() {
            return this.clonePanelInsideNode || this.cloneForm.nodeId
        },
        vitalsSeriesCodesSerialized() {
            if (!this.panel.vitals) {
                return null
            }
            return JSON.stringify(this.panel.vitals.series.map((s) => s.code))
        },
        activeVitalsSeries() {
            if (!this.panel.vitals) {
                return []
            }
            return this.panel.vitals.series.filter((s) => !s.is_deleted)
        },
        customFormEditorRoute() {
            if (!this.panel.id || !this.caseID || !this.nodeID) {
                return null
            }
            return {
                name: "CustomFormEditor",
                params: {
                    panelID: this.panel.id,
                    nodeID: this.nodeID,
                    caseID: this.caseID,
                },
            }
        },
        referenceDocumentBusy() {
            return this.referenceDocumentUploading || (
                this.referenceDocumentUpload &&
                this.referenceDocumentUpload.file_processing_status === 'in_progress'
            )
        },
        // The label for the AI-persona generation button.
        generateChatPersonaBtnLabel() {
            if (this.panel.ai_analysis_config.persona_details) {
                // The user has already entered details, so label it as an 'enhance' button.
                return "Enhance this persona"
            }
            return "Generate an example"
        },
        imageHotspotsFormFieldsExtra() {
            return [
                {
                    name: "Discovery activity mode",
                    type: "checkbox",
                    key: "indicate_selected_choices",
                    positionIndent: false,
                    subtext:
                        "Show the learner how many hotspots they've found, and fade out hotspots they've explored.",
                    condensedStyle: true,
                },
                {
                    key: "choice_count_readout_mode",
                    type: "radio",
                    options: [
                        {
                            value: "all",
                            label: "Show a count of all hotspots discovered",
                        },
                        {
                            value: "correct_only",
                            label: "Only count correct hotspots",
                        },
                    ],
                    isSubOption: true,
                    hidden(value) {
                        return !value.indicate_selected_choices
                    },
                },
                {
                    name: "Only score the learner's first response",
                    key: "score_first_response_only",
                    type: "checkbox",
                    subtext: "When checked, only the learner's first selection will count toward their overall score.",
                },
                {
                    name: "Display full screen",
                    type: "checkbox",
                    key: "display_full_screen",
                    positionIndent: false,
                    description: "Display the hotspots activity in fullscreen.",
                    subtext:
                        "When enabled, the header and subheader will be displayed in the bottom right hand corner of the screen.",
                },
                {
                    name: "Enable tabbing to hotspots",
                    type: "checkbox",
                    key: "enable_hotspot_tabbing",
                    positionIndent: false,
                    description:
                        "When checked, learners will be able to use the tab button on their keyboards to select hotspots.",
                },
                {
                    name: "360° Viewport Height",
                    type: "number",
                    key: "height",
                    placeholder: "600",
                    unit: "pixels",
                    required: false,
                    hidden(value) {
                        return value.hotspots_content_type == "2d_image"
                    },
                    disabled(value) {
                        return value.display_full_screen
                    },
                    displayInline: true,
                },
            ]
        },
        threeDVideoFormFields() {
            return [
                {
                    placeholder: "Screen reader scene description",
                    key: "accessibility_description",
                    subtext: "An alternative description of the 360° scene for learners using screen readers.",
                    type: "textarea",
                    rows: 2,
                    maxlength: 10000,
                },
                {
                    name: "Display full screen",
                    type: "checkbox",
                    key: "display_full_screen",
                    positionIndent: false,
                    subtext:
                        "When enabled, the header and subheader will be displayed in the bottom right hand corner of the screen.",
                },
                {
                    name: "Video height",
                    key: "height",
                    type: "number",
                    placeholder: "600",
                    unit: "pixels",
                    displayInline: true,
                    disabled(value) {
                        return value.display_full_screen
                    },
                },
            ]
        },
        textResponseFormFields() {
            let vm = this
            return [
                {
                    name: "Show feedback before continuing",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    subtext: "Show AI-generated feedback on this screen before moving on.",
                },
                {
                    name: "Allow learners to change their response",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    subtext:
                        "Allow learners to change their response when revisiting this screen, or after seeing feedback.",
                },
                {
                    name: "Response is required",
                    key: "textarea_required",
                    type: "checkbox",
                    subtext: "Check this box if the learner must respond to this inquiry before continuing.",
                },
                {
                    name: "Continue from previous text response inquiry",
                    key: "textarea_continue_from_prev",
                    type: "checkbox",
                },
                {
                    key: "linked_response_panel",
                    required(item) {
                        return item.textarea_continue_from_prev
                    },
                    type: "search",
                    options: vm.textResponsePanelsFlat,
                    labelField: "title",
                    valueField: "id",
                    searchFields: ["title"],
                    maxItems: 1,
                    placeholder: "Select an inquiry to continue from...",
                    hidden(item) {
                        return !item.textarea_continue_from_prev
                    },
                },
                {
                    type: "spacer",
                },
                {
                    name: "Number of Rows Displayed",
                    key: "textarea_rows",
                    placeholder: "Auto",
                    type: "number",
                    displayInline: true,
                },
                {
                    name: "Maximum Characters",
                    description: "Leave this field blank if you don't want learner response length to be limited.",
                    key: "character_limit",
                    placeholder: "No limit",
                    type: "number",
                    displayInline: true,
                },
                {
                    name: "Text Box Placeholder",
                    key: "textarea_placeholder",
                    placeholder: "A placeholder message shown to learners before they enter their response",
                    type: "textarea",
                    rows: 2,
                    displayInline: true,
                },
                // Legacy feature - only shown if the panel already has this setting turned on to
                // support legacy functionality.
                {
                    name: "Analyze response for keywords",
                    key: "analyze_text_responses",
                    type: "checkbox",
                    condensedStyle: true,
                    badge: "Legacy",
                    hidden() {
                        return !vm.savedPanel.analyze_text_responses
                    },
                },
            ]
        },
        selectionPanelFormFields() {
            return [
                {
                    name: "Reveal correctness of choices on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                },
                {
                    name: "Allow learners to deselect choices after making them",
                    key: "allow_deselecting_multi",
                    type: "checkbox",
                    subtext: "Turn this setting off to make selections of choices permanent.",
                    hidden(item) {
                        return item.panel_type !== "multiselect"
                    },
                },
                {
                    name: "Only score the learner's first response",
                    key: "score_first_response_only",
                    type: "checkbox",
                    subtext: "When checked, only the learner's first selection will count toward their overall score.",
                },
                {
                    name: "Allow learners to change their selections when revisiting this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    subtext:
                        "When checked, learners will be allowed to change their responses to this inquiry when revisiting this screen.",
                },
                {
                    name: "Record learner's selections instantly",
                    key: "record_selections_instantly",
                    type: "checkbox",
                    subtext: "Turn this setting off if you want choices to be submitted as a complete set.",
                },
                {
                    name: "Show feedback before continuing",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    hidden(item) {
                        return item.record_selections_instantly
                    },
                },
            ]
        },
        categoriesPanelFormFields() {
            return [
                {
                    name: "Dropzone size",
                    key: "dropzone_size",
                    type: "select",
                    description: "Adjust the size of the dropzones that items can be dragged into.",
                    options: [
                        {
                            label: "Small",
                            value: "small",
                        },
                        {
                            label: "Medium",
                            value: "medium",
                        },
                        {
                            label: "Large",
                            value: "large",
                        },
                        {
                            label: "Fill Containing Block",
                            value: "fill",
                        },
                    ],
                    autoWidth: true,
                },
            ]
        },
        categoriesPanelFormFieldsExtra() {
            return [
                {
                    name: "Freeze items after learner submits a response",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    subtext: "Check this box to prevent learners from moving items after they submit a response.",
                },
                {
                    name: "Reveal correctness of placed items on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    subtext: "Control whether learners see the correctness of their item placements.",
                },
                {
                    name: "Show feedback before continuing",
                    subtext: "Show feedback and correctness of item placements before moving on.",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    hidden(value) {
                        return !value.show_correctness
                    },
                },
            ]
        },
        customFormPanelFields() {
            return [
                {
                    name: "Disable input fields after learner submits response",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    description:
                        "Check this box to prevent learners from editing their responses after they submit a response.",
                },
                {
                    name: "Require that all fields are filled out",
                    key: "all_items_required",
                    type: "checkbox",
                    description: "Check this box to force learners to enter a value into each field in the form.",
                },
                {
                    name: "Allow learners to change their response when revisiting this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                    description:
                        "When checked, learners will be allowed to change their responses to this inquiry when revisiting this screen.",
                },
                {
                    name: "Clear inputs when revisiting this screen",
                    description: "When checked, all form inputs will be set to blank when returning to this screen.",
                    key: "clear_inputs_when_returning",
                    type: "checkbox",
                    hidden(value) {
                        return !value.allow_answer_changing
                    },
                },
                {
                    name: "Reveal correctness of fields on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    description: "Control whether learners see the correctness of their fields.",
                },
                {
                    name: "Show feedback before continuing",
                    description: "Show feedback and correctness of the form before moving on.",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    hidden(value) {
                        return !value.show_correctness
                    },
                },
            ]
        },
        stepOrderPanelFormFields() {
            return [
                {
                    name: "Freeze steps after learner presses submit",
                    key: "disable_input_after_submit",
                    type: "checkbox",
                    subtext: "Check this box to prevent learners from reordering their steps after they press submit.",
                },
                {
                    name: "Reveal correctness of steps on this screen",
                    key: "show_correctness",
                    type: "checkbox",
                    subtext: "Control whether learners see the correctness of their steps after submitting.",
                },
                {
                    name: "Show feedback before continuing",
                    subtext: "Show feedback and correctness of steps before moving on.",
                    key: "show_feedback_before_continuing",
                    type: "checkbox",
                    hidden(value) {
                        return !value.show_correctness
                    },
                },
            ]
        },
        voiceRecordingPanelFields() {
            return [
                {
                    name: "Max Length",
                    key: "recording_time_limit_sec",
                    description: "The maximum length of the learner's recording.",
                    type: "select",
                    options: [
                        {
                            label: "30 Seconds",
                            value: 30,
                        },
                        {
                            label: "1 Minute",
                            value: 60,
                        },
                        {
                            label: "2 Minutes",
                            value: 120,
                        },
                        {
                            label: "3 Minutes",
                            value: 180,
                        },
                        {
                            label: "4 Minutes",
                            value: 240,
                        },
                        {
                            label: "5 Minutes",
                            value: 300,
                        },
                        {
                            label: "10 Minutes",
                            value: 600,
                        },
                    ],
                    required: true,
                },
                {
                    name: "Transcription Language",
                    key: "transcribe_audio_language",
                    type: "select",
                    options: languageCodes,
                },
            ]
        },
        voiceConversationPanelFields() {
            return [
                {
                    name: "Voice",
                    key: "voice_name",
                    type: "select",
                    options: [
                        // Add voice names only supported by the realtime API.
                        { label: "Marin", value: "marin" },
                        { label: "Cedar", value: "cedar" }, 
                    ].concat(this.aiVoiceNames),
                },
            ]
        },
        voiceConversationPanelExtraFields() {
            return [
                {
                    name: "Who starts the conversation?",
                    key: "conversation_start_mode",
                    type: "radio",
                    options: [
                        { value: "learner", label: "🗣️ Learner Starts" },
                        {
                            value: "ai",
                            label: "🤖 AI Starts",
                            badge: "NEW",
                            badgeClass: "badge-highlight"
                        },
                    ],
                },
                {
                    name: "Max Length",
                    key: "recording_time_limit_sec",
                    description: "The maximum length of the conversation.",
                    type: "select",
                    options: [
                        {
                            label: "30 Seconds",
                            value: 30,
                        },
                        {
                            label: "1 Minute",
                            value: 60,
                        },
                        {
                            label: "2 Minutes",
                            value: 120,
                        },
                        {
                            label: "3 Minutes",
                            value: 180,
                        },
                        {
                            label: "5 Minutes",
                            value: 300,
                        },
                        {
                            label: "10 Minutes",
                            value: 600,
                        },
                        {
                            label: "20 Minutes",
                            value: 1200,
                        },
                    ],
                    required: true,
                },
                {
                    name: "Transcription Language",
                    key: "transcribe_audio_language",
                    type: "select",
                    options: languageCodes,
                },
                {
                    name: "Listen longer to avoid cutting the learner off mid-thought",
                    type: "switch",
                    subtext: "Note: Turning this setting on may create more response latency.",
                    key: "use_semantic_voice_detection",
                    positionIndent: true,
                },
            ]
        },
        customGPTFormFieldsPart2() {
            return [
                {
                    name: "Mood & Emotion",
                    key: "emotion",
                    type: "select",
                    options: [
                        {
                            value: "Neutral, succinct",
                            label: "Neutral, succinct",
                        },
                        {
                            value: "Irritable, angry",
                            label: "Irritable, angry",
                        },
                        {
                            value: "Confused, disoriented",
                            label: "Confused, disoriented",
                        },
                        {
                            value: "Cold, professional",
                            label: "Cold, professional",
                        },
                        {
                            value: "Impatient, annoyed",
                            label: "Impatient, annoyed",
                        },
                        {
                            value: "custom",
                            label: "Custom",
                        },
                    ],
                },
                {
                    name: " ",
                    type: "textarea",
                    key: "custom_emotion",
                    placeholder: "Describe the tone the AI should take on...",
                    rows: 3,
                    hidden(value) {
                        return value.emotion !== "custom"
                    },
                },
                {
                    name: "Respond in both voice and text",
                    subtext: "Note: When voice is enabled, the AI will take slightly longer to respond.",
                    key: "respond_with_voice",
                    type: "switch",
                    positionIndent: true,
                },
                {
                    name: "Voice",
                    key: "voice_name",
                    type: "select",
                    hidden(value) {
                        return !value.respond_with_voice
                    },
                    options: aiVoiceNames,
                    labelField: "name",
                    valueField: "value",
                },
            ]
        },
    },
    watch: {
        cloneCaseId: function (newVal) {
            this.cloneForm.nodeId = null
            if (newVal) {
                this.fetchCloneNodeOptions()
            }
        },
        referenceDocumentUpload(newVal, oldVal) {
            if (!newVal && oldVal && oldVal.id) {
                this.clearReferenceDocumentStatusTimeouts()
                delete this.panel.ai_analysis_config.reference_document
            }
        },
        panel: {
            deep: true,
            // Track whether the panel config has been edited by the user.
            handler: function (newVal, oldVal) {
                if (newVal.id !== oldVal.id) {
                    this.panelIsEdited = false
                    return
                }
                this.panelIsEdited = true
            },
        },
    },
    methods: {
        // Open the editor for a panel and fetch it.
        open(panel) {
            this.openEditPanelModal(panel)
            this.fetchPanel(panel.id)
        },
        // Open the delete modal for a panel and fetch it.
        openDelete(panel) {
            this.openDeletePanel()
            this.fetchPanel(panel.id)
        },
        // Open the edit modal for a panel.
        openEditPanelModal(panel) {
            if (panel.panel_type === "linked_response") {
                // Special case - fetch a list of all linkable panels to be
                // displayed in the editor.
                this.fetchLinkablePanels()
            } else if (panel.panel_type === "textarea") {
                this.fetchTextResponsePanels()
            }
            if (this.panel.id !== panel.id) {
                // Default to the content tab when switching to edit a new panel.
                this.currentTab = "content"
            }
            this.$refs["panel-editor-modal"].show()
        },
        // Open the delete modal for a panel.
        openDeletePanel() {
            this.closeEditModals()
            $("#delete-panel-modal").modal("show")
        },
        // Open the panel type switcher modal - only used for certain panel types.
        openChangePanelTypeModal() {
            this.closeEditModals()
            this.$refs.panelTypeSelector.open(this.panel)
        },
        // Clears the timer used to poll for the reference document processing status
        clearReferenceDocumentStatusTimeouts() {
            this.referenceDocumentStatusTimeoutIds.forEach((timeoutId) => {
                clearTimeout(timeoutId)
            })
            this.referenceDocumentStatusTimeoutIds = []
        },
        // Saves the reference document to to ai_analysis_config json
        setReferenceDocument(referenceDocumentUpload) {
            let normalizedReferenceDocument = {}
            // These are the only properties that should be persisted in the json
            normalizedReferenceDocument.id = referenceDocumentUpload.id
            normalizedReferenceDocument.access_key = referenceDocumentUpload.access_key
            normalizedReferenceDocument.name = referenceDocumentUpload.name
            normalizedReferenceDocument.file_processing_status = referenceDocumentUpload.file_processing_status
            this.$set(this.panel.ai_analysis_config, "reference_document", normalizedReferenceDocument)
        },
        // Kicks off the http request to start the chain for the reference document conversion to markdown.
        startReferenceDocumentConversion() {
            let referenceDocumentUpload = this.referenceDocumentUpload
            if (!referenceDocumentUpload || !referenceDocumentUpload.id || !referenceDocumentUpload.access_key) {
                if (referenceDocumentUpload) {
                    this.$set(referenceDocumentUpload, "file_processing_status", "failed")
                }
                this.$notify({
                    group: "main",
                    type: "danger",
                    text: "An unexpected error occurred and the document could not be processed. Please try uploading again. If the error persists, contact support.",
                    ignoreDuplicates: true,
                    duration: 6000,
                    data: {},
                })
                return
            }

            this.clearReferenceDocumentStatusTimeouts()
            this.$set(referenceDocumentUpload, "file_processing_status", "in_progress")
            this.setReferenceDocument(referenceDocumentUpload)

            http.put(`/api/files/${referenceDocumentUpload.id}/document-conversion/`, {
                access_key: referenceDocumentUpload.access_key,
            })
                .then(() => {
                    this.pollReferenceDocumentConversionStatus(referenceDocumentUpload)
                })
                .catch(() => {
                    this.$set(referenceDocumentUpload, "file_processing_status", "failed")
                    this.$notify({
                        group: "main",
                        type: "danger",
                        text: "Document processing failed to start. Please try again.",
                        ignoreDuplicates: true,
                        duration: 6000,
                        data: {},
                    })
                })
        },
        // Polls the status of the reference document and calls setReferenceDocument once successful.
        pollReferenceDocumentConversionStatus(referenceDocumentUpload = this.referenceDocumentUpload) {
            http.get(`/api/files/${referenceDocumentUpload.id}/document-conversion/`, {
                params: {
                    access_key: referenceDocumentUpload.access_key,
                }
            }).then((response) => {
                this.$set(referenceDocumentUpload, "file_processing_status", response.data.file_processing_status)
                if (response.data.file_processing_status === "in_progress") {
                    let timeoutId = window.setTimeout(() => {
                        this.pollReferenceDocumentConversionStatus(referenceDocumentUpload)
                    }, 3000)
                    this.referenceDocumentStatusTimeoutIds.push(timeoutId)
                } else {
                    this.setReferenceDocument(referenceDocumentUpload)
                }
            }).catch(() => {
                this.$set(referenceDocumentUpload, "file_processing_status", "failed")
                this.setReferenceDocument(referenceDocumentUpload)
            })
        },
        // Fetch a panel by its ID.
        fetchPanel(id, clearPanel = true) {
            this.modalLoading = true
            this.clearReferenceDocumentStatusTimeouts()
            if (clearPanel) {
                // Reset the panel during loading.
                this.panel = {
                    vitals: null,
                    elements: [],
                    ai_analysis_config: {},
                }
                this.referenceDocumentUpload = null
            }
            let vm = this
            return http.get("/api/panels/" + id + "/").then(function (response) {
                vm.panel = response.data
                vm.referenceDocumentUpload = null
                if (
                    vm.panel.ai_analysis_config &&
                    vm.panel.ai_analysis_config.reference_document &&
                    vm.panel.ai_analysis_config.reference_document.id
                ) {
                    let refDoc = vm.panel.ai_analysis_config.reference_document
                    vm.referenceDocumentUpload = {
                        id: refDoc.id,
                        access_key: refDoc.access_key,
                        name: refDoc.name,
                        file_processing_status: refDoc.file_processing_status,
                        content_type: "application/octet-stream",
                    }
                    if (vm.referenceDocumentUpload.file_processing_status === "in_progress") {
                        vm.pollReferenceDocumentConversionStatus(vm.referenceDocumentUpload)
                    }
                }
                vm.savedPanel = Object.assign({}, response.data)
                vm.modalLoading = false
                vm.showKeyInteractions =
                    Array.isArray(vm.panel.chat_intents) && vm.panel.chat_intents.some((i) => !i.is_deleted)

                // Separate out pre-content video elements from all other elements, so they can be
                // edited separately.
                vm.$set(
                    vm.panel,
                    "preContentVideoElements",
                    vm.panel.elements.filter((e) => e.is_pre_content_video)
                )
                vm.panel.elements = vm.panel.elements.filter((e) => !e.is_pre_content_video)

                if (vm.panel.panel_type === "vitals") {
                    vm.panel.vitals = vm.panel.vitals || {}
                } else if (vm.panel.panel_type == "custom" && vm.panel.sub_header) {
                    vm.panel.hasExistingSubheader = true
                }
                vm.panelIsEdited = false
            })
        },
        // Save an existing panel.
        savePanel(panelObj = null, showNotification = true) {
            let vm = this
            let panel = panelObj ? panelObj : this.panel

            // Special case - use a different method to save panels of type 'vitals'.
            if (panel.panel_type === 'vitals') {
                this.saveVitalsPanel()
                return
            }

            // Special case - validate text response AI analysis form fields.
            if (
                ['textarea', 'voice_recording'].includes(panel.panel_type) &&
                panel.enable_ai_analysis &&
                !panel.ai_analysis_config.expert_response &&
                !panel.ai_analysis_config.extra_instructions &&
                !panel.ai_analysis_config.scoring_rubric &&
                !panel.ai_analysis_config.full_context_enabled &&
                !(
                    panel.ai_analysis_config.reference_document &&
                    panel.ai_analysis_config.reference_document.id
                )
            ) {
                panel.enable_ai_analysis = false
            } else if (
                ['voice_conversation', 'chat_v2'].includes(panel.panel_type) &&
                panel.enable_ai_analysis &&
                !panel.ai_analysis_config.feedback_rubric &&
                !panel.ai_analysis_config.scoring_rubric &&
                !panel.ai_analysis_config.full_context_enabled &&
                !(
                    panel.ai_analysis_config.reference_document &&
                    panel.ai_analysis_config.reference_document.id
                )
            ) {
                panel.enable_ai_analysis = false
            }

            // If the panel doesn't have a pre-content video, make sure the
            // 'show a video first' option isn't enabled.
            if (!panel.preContentVideoElements.length) {
                panel.show_video_first = false
            }

            this.modalLoading = true

            // Clone the panel object.
            panel = JSON.parse(JSON.stringify(panel))

            // Add back pre-content video elements to the list (which were separated upon load, for editing).
            panel.preContentVideoElements = panel.preContentVideoElements.map((e) => {
                return { ...e, is_pre_content_video: true }
            })
            panel.elements = panel.elements.concat(panel.preContentVideoElements)
            panel.elements.forEach((el, index) => {
                // Remove all signed URLs from elements - don't need to include these in the payload.
                if (el.stored_file) {
                    delete panel.elements[index].stored_file.signed_url
                }
                if (el.height === "") {
                    el.height = null
                }
            })

            // Remove blank elements.
            panel.elements = Utilities.filterBlankElements(panel.elements)
            panel.choices.forEach((choice, index) => {
                panel.choices[index].elements = Utilities.filterBlankElements(choice.elements)
            })

            // Set any blank scores to zero.
            panel.choices.filter((choice) => !choice.score).forEach((choice) => (choice.score = 0))
            panel.chat_intents.filter((intent) => !intent.score).forEach((intent) => (intent.score = 0))
            // Set blank panel height to zero.
            if (panel.height === "") {
                panel.height = null
            }
            // Remove any existing blank intents.
            if (panel.panel_type === "chat_v2") {
                panel.chat_intents = panel.chat_intents.filter((intent) => intent.ai_description || intent.label)
            }

            // Remove blank fields.
            if (panel.linked_response_panel == "") {
                panel.linked_response_panel = null
            }
            if (!panel.textarea_rows) {
                panel.textarea_rows = null
            }
            if (!panel.character_limit) {
                panel.character_limit = null
            }
            if (!panel.max_ai_score) {
                panel.max_ai_score = 0
            }
            return new Promise((resolve) => {
                http
                .put("/api/panels/" + panel.id + "/", panel)
                .then(function () {
                    vm.modalLoading = false
                    vm.closeEditModals()
                    if (showNotification) {
                        vm.$notify({
                            group: "main",
                            type: "success",
                            text: "Content updated.",
                            data: {},
                        })
                    }
                    vm.$emit("updated", panel.id)
                    if (!vm.preserveMap) {
                        vm.$store.commit("mapRequiresUpdate")
                    }
                    resolve(true)
                })
                .finally(() => (vm.modalLoading = false))
            })
        },
        // Handle a new panel type being selected. Update the type of the
        // panel to the newly selected one.
        panelTypeSelectedCb(newPanel) {
            this.modalLoading = true
            let vm = this
            http
            .put(`/api/panels/${this.panel.id}/basic/`, {
                panel_type: newPanel.panel_type
            })
            .then(() => {
                vm.modalLoading = false
                vm.$notify({
                    group: "main",
                    type: "success",
                    text: "Content type updated.",
                    data: {},
                })
                // Hide the panel type selector.
                vm.$refs.panelTypeSelector.hide()
                // Re-open the editor.
                vm.open(this.panel)
                vm.$emit("updated", vm.panel.id)
                if (!vm.preserveMap) {
                    vm.$store.commit("mapRequiresUpdate")
                }
            })
        },
        // Handle the panel type selector modal being explicitly cancelled by
        // the user. Open the editor for the panel. This isn't called when the
        // panel type selector modal is dismissed by other methods, only when
        // the 'cancel' button is pressed.
        panelTypeChangeCancelledCb() {
            this.openEditPanelModal(this.panel)
        },
        // Save an existing panel of type 'vitals'.
        saveVitalsPanel() {
            this.modalLoading = true
            let formData = new FormData()

            // Clone panel object.
            let panelJson = JSON.stringify(this.panel)
            formData.append("panel", panelJson)

            if (this.panel.vitals.csv_file && this.panel.vitals.csv_file.file) {
                formData.append("csv", this.panel.vitals.csv_file.file)
            }

            let vm = this
            http.put("/api/panels/" + this.panel.id + "/vitals/", formData)
                .then(function () {
                    vm.modalLoading = false
                    $("#edit-vitals-panel-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Content updated.",
                        data: {},
                    })
                    vm.$emit("updated", vm.panel.id)
                    vm.closeEditModals()
                })
                .catch(function () {
                    vm.modalLoading = false
                })
        },
        // Delete an existing panel.
        deletePanel() {
            this.modalLoading = true
            let vm = this
            http.put("/api/panels/" + this.panel.id + "/remove/")
                .then(function () {
                    vm.modalLoading = false
                    $("#delete-panel-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Content deleted.",
                        data: {},
                    })
                    vm.$emit("deleted", vm.panel.id)
                    vm.$emit("updated", vm.panel.id)
                    if (!vm.preserveMap) {
                        vm.$store.commit("mapRequiresUpdate")
                    }
                })
                .catch(function () {
                    vm.modalLoading = false
                })
        },
        // Attach a vitals csv file to the panel object.
        attachVitalsCSV(csvFile) {
            this.panel.vitals = {
                csv_file: csvFile,
            }
        },
        // Close the edit modal.
        closeEditModals() {
            this.$refs["panel-editor-modal"].hide()
        },
        // Returns an icon class for a given panel type.
        panelTypeIcon(panelType) {
            return panelTypeIcons[panelType]
        },
        // Returns true if the given panel type.
        panelTypeWalkthroughVideoUrl(panelType) {
            return panelTypes.find(p => p.value == panelType)?.walkthroughVideoUrl
        },
        // Returns a label for a given panel type.
        panelTypeLabel(panelType) {
            let label = ""
            try {
                label = panelTypeLabels[panelType]
            } catch (error) {
                label = "Content"
            }
            return label
        },
        // Shift an item in a list by an increment.
        shiftListItem(index, increment, list) {
            // Don't do anything if the item is already at the start or end of the array.
            if ((index == 0 && increment == -1) || (index + 1 >= list.length && increment == 1)) {
                return
            }
            // Remove the choice from the choice array.
            let item = list.splice(index, 1)[0]
            // Put it back at the new position.
            list.splice(index + increment, 0, item)
        },
        // Remove an item from a list by its index.
        removeListItemByIndex(list, index) {
            list.splice(index, 1)
        },
        // Create a choice category on the currently selected panel.
        createChoiceCategory() {
            this.panel.choice_categories.push({
                name: "",
                tempId: String(Math.floor(Math.random() * 10000000)),
            })
        },
        // Fetch all text response panels for the case - used for linked response panels.
        fetchTextResponsePanels() {
            let vm = this
            http.get("/api/cases/" + this.caseID + "/panels/", {
                params: {
                    panelType: "textarea",
                },
            }).then(function (response) {
                vm.textResponsePanels = response.data
            })
        },
        // Fetch all panels that can be used with panels of type 'linked_response'.
        fetchLinkablePanels() {
            let allowedPanelTypes = [
                "textarea",
                "voice_recording",
                "multiselect",
                "singleselect",
                "step_order",
                "categories",
                "custom_form",
                "chat",
                "chat_v2",
                "voice_conversation",
            ]
            let vm = this
            http.get("/api/cases/" + this.caseID + "/panels/", {
                params: {
                    panelType: allowedPanelTypes.join(","),
                },
            }).then(function (response) {
                vm.linkablePanels = response.data
            })
        },
        // Open the clone modal for a given panel.
        async openClone(panel = null) {
            if (panel) {
                await this.fetchPanel(panel.id)
            }
            this.$refs.clonePanelForm.resetValidate()
            this.closeEditModals()
            // If edit is locked, default to cloning outside the node so the user
            // is prompted to select a library item.
            if (this.editLocked) {
                this.clonePanelInsideNode = false
            }
            this.fetchCloneNodeOptions()
            // Fetch an initial set of items.
            this.$refs.itemSelector.searchDebounced("")
            $("#clone-panel-modal").modal("show")
            // If no library item is selected, select the library item currently being viewed.
            if (this.cloneForm.libraryItem === null && !this.editLocked) {
                // This is a hack to force the current library item to be selected in the library item
                // selector. We need to fetch the full representation of the associated case in order to
                // display the cover image.
                await this.$store.dispatch("fetchCase", this.caseID)
                let caseObj = this.$store.getters.case
                this.$refs.itemSelector.forceSelectItem({
                    item_type: "owned_case",
                    title: caseObj.public_title,
                    case: caseObj,
                })
            }
        },
        // Fetch a list of nodes that the current panel can be cloned into.
        fetchCloneNodeOptions() {
            if (!this.cloneCaseId) {
                return
            }
            let vm = this
            this.cloneNodeOptionsLoading = true
            http.get(`/api/cases/${this.cloneCaseId}/nodes/`)
                .then((response) => {
                    vm.cloneNodeOptions = response.data
                })
                .finally(() => {
                    vm.cloneNodeOptionsLoading = false
                })
        },
        // Clone an existing panel.
        clonePanel() {
            if (!this.clonePanelFormValid) {
                return
            }
            let nodeId
            if (this.clonePanelInsideNode) {
                nodeId = null
            } else {
                nodeId = this.cloneForm.nodeId
            }
            this.modalLoading = true
            let vm = this
            http.put(`/api/panels/${this.panel.id}/clone/`, {
                targetNodeID: nodeId,
            })
                .then(() => {
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Content cloned.",
                        data: {},
                    })
                    vm.$emit("updated")
                    $("#clone-panel-modal").modal("hide")
                })
                .finally(() => (vm.modalLoading = false))
        },
        // Navigate to the hotspots editor for an image hotspots panel.
        async navToHotspotsEditor() {
            if (!this.panel.id || !this.caseID || !this.nodeID) {
                return
            }
            if (!this.editLocked && this.panelIsEdited) {
                // Save the panel before navigating to the editor.
                let panelSaved = await this.savePanel(null, false)
                if (!panelSaved) {
                    return
                }
            }
            let hotspotsEditorRoute = {
                name: "ImageHotspotsEditor",
                params: {
                    panelID: this.panel.id,
                    nodeID: this.nodeID,
                    caseID: this.caseID,
                },
            }
            this.$router.push(hotspotsEditorRoute)
        },
        // Add a new chat intent.
        createIntent(index) {
            let intent = {
                message: "",
                code: "",
                correct: true,
                detection_type: "intent",
                ai_description: "",
            }
            this.panel.chat_intents.splice(index, 0, intent)
            let vm = this
            this.$nextTick(() => {
                vm.$refs.intentMessage[this.panel.chat_intents.length - 1].focus()
            })
        },
        // Remove an intent (mark it as deleted).
        removeIntent(intent) {
            this.$set(intent, "is_deleted", true)
        },
        // Generate or enhance the current AI persona description.
        generateChatPersona(panel) {
            if (panel.isGeneratingPersona) {
                return
            }
            this.$set(panel, "isGeneratingPersona", true)
            let payload = {
                organizationId: this.currentOrganizationId,
                panelId: panel.id,
                currentPrompt: panel.ai_analysis_config.persona_details,
            }
            let vm = this
            http.post("/api/generate-persona/", payload)
                .then((response) => {
                    panel.ai_analysis_config.persona_details = response.data.content
                })
                .catch((error) => {
                    vm.$set(panel, "errorMessage", error.response.data.message)
                })
                .finally(() => {
                    panel.isGeneratingPersona = false
                })
        },
        showIntentFeedbackField(intent, index) {
            this.$set(intent, "showFeedbackInput", true)
            // Bring the input into focus.
            this.$nextTick(() => this.$refs.intentFeedbackMessageInput[index].focus())
        },
        createVitalsSeries() {
            this.panel.vitals.series.push({
                code: "",
                hex_color: this.vitalsColorOptions[0],
                triggers: [],
            })
        },
        setVitalsColor(series, hexColor) {
            series.hex_color = hexColor
        },
        shiftVitalsSeriesPosition(index, increment) {
            // Don't do anything if series can't be moved further up or down.
            if ((index == 0 && increment == -1) || (index + 1 >= this.panel.vitals.series.length && increment == 1)) {
                return
            }
            // Remove the choice from the choice array.
            let series = this.panel.vitals.series.splice(index, 1)[0]
            // Put it back at the new position.
            this.panel.vitals.series.splice(index + increment, 0, series)
        },
        removeVitalsSeries(series) {
            this.$set(series, "is_deleted", true)
        },
        addTrigger(series) {
            series.triggers.push({
                low_boundary: null,
                high_boundary: null,
                change_type: "replace_with",
                trigger_type: "choice",
            })
        },
        removeTrigger(series, index) {
            series.triggers.splice(index, 1)
        },
        createTextEntity(panel) {
            panel.text_entities.push({
                correct: true,
                score: 0,
                display_label: "",
                keywords: [{ value: "" }],
            })
        },
        createKeywordVariation(textEntity) {
            textEntity.keywords.push({ value: "" })
        },
        removeTextEntity(panel, index) {
            panel.text_entities.splice(index, 1)
        },
        removeTextEntityKeyword(textEntity, index) {
            textEntity.keywords.splice(index, 1)
        },
    },
    mounted() {
        let vm = this
        $('[data-panel-edit-modal="true"]').on("shown.bs.modal", function () {
            vm.$store.commit("setEditPanelModalIsOpen", true)
        })
        $('[data-panel-edit-modal="true"]').on("hidden.bs.modal", function () {
            vm.$store.commit("setEditPanelModalIsOpen", false)
            vm.clearReferenceDocumentStatusTimeouts()
        })
    },
    beforeDestroy() {
        this.clearReferenceDocumentStatusTimeouts()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/nodemap.scss";
@import "@/assets/styles/paneledit.scss";

.tabs-layout-container {
    padding-left: 1.3rem;
    padding-right: 1.3rem;
    padding-top: 1.15rem;
    padding-bottom: 0.8rem;
}

.settings-nav-link {
    text-decoration: none;
    color: $gray-600;
    border-bottom: 2px solid transparent;
    transition: $transition-base;
    font-weight: 500;
    margin-top: 8px;
    margin-right: 24px;
    padding-bottom: 14px;
    padding-right: 3px;
    @media (min-width: $breakpoint-sm) {
        margin-top: 0px;
    }
    &:hover:not(.active) {
        color: darken($secondary, 25%);
    }
    &.active {
        border-bottom: 2px solid $primary;
        color: $primary !important;
    }
}

.settings-nav-container {
    margin-bottom: -1px;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 20px;
}

.panel-type-badge {
    color: $gray-600;
}

.vitals-color-dropdown {
    max-width: 6rem;
    min-width: 5rem !important;
}

.vitals-color-preview {
    height: 20px;
    width: 30px;
}

.vitals-boundary-input {
    max-width: 100px;
}

.vitals-glyph-spacer {
    width: 24px;
    text-align: center;
}

.vitals-boundary-code {
    width: 20px;
    white-space: nowrap;
    margin-right: 5px;
    overflow: hidden;
}

.category-name-input {
    max-width: 600px;
}

.icon-glyphicons-basic-squares {
    vertical-align: -1px;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables.scss";

// Rich text editor.
.ProseMirror {
    height: auto;
    min-height: 100px;
}

.vitals-code {
    p {
        margin-bottom: 0px !important;
    }
}

.alt-text-input {
    max-width: 350px;
}
</style>
